import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FirebaseContext, api } from 'common';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import { ref as databaseRef, onValue, update as updateDatabase } from 'firebase/database';
import { getDownloadURL, ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import { BRAND_ACCENTS, DEFAULT_EAT_HEADERS, EAT_CUISINES, MENU_CATEGORY_PRESETS, categoryLabel, cuisineLabel, menuSectionId } from '../common/eatCatalog';
import GoogleMapsAutoComplete from '../components/GoogleMapsAutoComplete';
import eatsLogo from '../assets/img/eatslogo.png';

const currency = (value) => `${Number(value || 0).toFixed(2)} EUR`;
// Branding Eats aligné VTC (or/noir) — remplace le vert MUI "success" par défaut.
const GOLD_CHIP_SX = { bgcolor: '#d4af37', color: '#000', fontWeight: 700 };
const DEFAULT_EAT_COMMISSION_RATE = 0.10;

const ALLERGEN_OPTIONS = [
  { id: 'gluten', label: 'Gluten' },
  { id: 'crustaces', label: 'Crustacés' },
  { id: 'oeufs', label: 'Oeufs' },
  { id: 'poisson', label: 'Poisson' },
  { id: 'arachides', label: 'Arachides' },
  { id: 'soja', label: 'Soja' },
  { id: 'lait', label: 'Lait' },
  { id: 'fruits-coque', label: 'Fruits à coque' },
  { id: 'celeri', label: 'Céleri' },
  { id: 'moutarde', label: 'Moutarde' },
  { id: 'sesame', label: 'Sésame' },
  { id: 'sulfites', label: 'Sulfites' },
  { id: 'lupin', label: 'Lupin' },
  { id: 'mollusques', label: 'Mollusques' }
];

const normalizeAllergens = (value) => Array.isArray(value) ? value.filter(Boolean) : [];
const allergenLabel = (id) => (ALLERGEN_OPTIONS.find(item => item.id === id) || {}).label || id;

const DAY_CONFIG = [
  { key: 'sunday', label: 'Dimanche' },
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' }
];

const numberValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getCommissionRate = (settings = {}, restaurant = {}) => {
  const configuredRate = numberValue(
    settings.restaurantCommissionPercent ??
    settings.platformCommissionPercent ??
    settings.restaurantCommissionRate ??
    restaurant.restaurantCommissionPercent ??
    restaurant.platformCommissionPercent ??
    restaurant.restaurantCommissionRate,
    NaN
  );
  if (Number.isFinite(configuredRate) && configuredRate > 1) return configuredRate / 100;
  if (Number.isFinite(configuredRate) && configuredRate >= 0) return configuredRate;
  return DEFAULT_EAT_COMMISSION_RATE;
};

const getRestaurantItemPrice = (item = {}, commissionRate = DEFAULT_EAT_COMMISSION_RATE) => {
  return numberValue(
    item.restaurantPrice ??
    item.basePrice ??
    item.originalPrice ??
    (item.commissionIncluded ? numberValue(item.price, 0) / (1 + commissionRate) : item.price),
    0
  );
};

const getClientItemPrice = (item = {}, commissionRate = DEFAULT_EAT_COMMISSION_RATE) => {
  return numberValue(item.customerPrice ?? (getRestaurantItemPrice(item, commissionRate) * (1 + commissionRate)), 0);
};

const parseOptionLines = (value = '') => String(value || '').split('\n').map(item => item.trim()).filter(Boolean);

const formatOptionLines = (value) => Array.isArray(value) ? value.join('\n') : '';

const parseOptionGroupsText = (value = '') => String(value || '')
  .split(/\n\s*\n/)
  .map(block => block.split('\n').map(line => line.trim()).filter(Boolean))
  .filter(lines => lines.length >= 2)
  .map(lines => {
    const [titleRaw, minRaw, maxRaw] = lines[0].split('|').map(part => part.trim());
    const title = titleRaw || 'Option';
    const min = Math.max(0, Number(minRaw || 1));
    const max = Math.max(min || 1, Number(maxRaw || minRaw || 1));
    return {
      id: makeId('option', title),
      title,
      min,
      max,
      required: min > 0,
      options: lines.slice(1)
    };
  })
  .filter(group => group.options.length);

const formatOptionGroupsText = (value) => Array.isArray(value) ? value.map(group => {
  const header = `${group.title || group.name || 'Option'}|${Number(group.min ?? 1)}|${Number(group.max ?? group.min ?? 1)}`;
  const options = Array.isArray(group.options) ? group.options : Array.isArray(group.values) ? group.values : [];
  return [header, ...options].join('\n');
}).join('\n\n') : '';

const getRestaurantAddress = (restaurant = {}) => restaurant.address || restaurant.restaurantAddress || restaurant.location?.add || restaurant.location?.address || restaurant.city || restaurant.zoneId || '';

const getRestaurantKbisUrl = (restaurant = {}) => restaurant.kbisUrl || restaurant.kbis || restaurant.documents?.kbisUrl || restaurant.documents?.kbis || '';
const getRestaurantRibUrl = (restaurant = {}) => restaurant.ribUrl || restaurant.rib || restaurant.documents?.ribUrl || restaurant.documents?.rib || '';
const getRestaurantIban = (restaurant = {}) => String(restaurant.iban || restaurant.IBAN || restaurant.documents?.iban || '').replace(/\s+/g, '').toUpperCase();
const getRestaurantBic = (restaurant = {}) => String(restaurant.bic || restaurant.BIC || restaurant.documents?.bic || '').replace(/\s+/g, '').toUpperCase();
const hasRibInfo = (restaurant = {}) => Boolean(getRestaurantRibUrl(restaurant) || getRestaurantIban(restaurant));
const hasRequiredRestaurantDocs = (restaurant = {}) => Boolean(getRestaurantKbisUrl(restaurant) && hasRibInfo(restaurant));

const getDefaultOpeningHours = () => DAY_CONFIG.reduce((hours, day) => {
  hours[day.key] = { open: true, start: '11:30', end: '22:30' };
  return hours;
}, {});

const getOpeningHours = (restaurant = {}) => ({ ...getDefaultOpeningHours(), ...(restaurant.openingHours || {}) });

const formatDaySchedule = (day = {}) => {
  if (day.open === false) return 'Fermé';
  const first = `${day.start || '11:30'} - ${day.end || '22:30'}`;
  if (day.split && day.start2 && day.end2) {
    return `${first} / ${day.start2} - ${day.end2}`;
  }
  return first;
};

const getOpeningSummary = (restaurant = {}) => {
  const hours = getOpeningHours(restaurant);
  const openDays = DAY_CONFIG.filter(day => hours[day.key] && hours[day.key].open !== false);
  if (!openDays.length) return 'Horaires non disponibles';
  const firstDay = openDays[0];
  return `Disponible ${firstDay.label} à ${hours[firstDay.key].start || '11:30'}`;
};

const cancelReasons = [
  'Article indisponible',
  'Restaurant ferme exceptionnellement',
  'Trop de commandes en cours',
  'Adresse hors zone de livraison',
  'Probleme technique ou paiement',
  'Demande du client'
];

const getNotificationPermission = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return window.Notification.permission;
};

const isNewRestaurantOrder = (order = {}) => ['PLACED', 'PAID'].includes(String(order.status || '').toUpperCase());

const getOrderTimestamp = (order = {}) => {
  const candidates = [order.createdAt, order.placedAt, order.deliveredAt, order.cancelledAt, order.updatedAt];
  for (const value of candidates) {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }
  return 0;
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return '-';
  try { return new Date(timestamp).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); } catch (e) { return '-'; }
};

const formatDateOnly = (timestamp) => {
  if (!timestamp) return '-';
  try { return new Date(timestamp).toLocaleDateString('fr-FR'); } catch (e) { return '-'; }
};

const toCsvCell = (value) => {
  const str = value === null || value === undefined ? '' : String(value);
  if (/[";\n]/.test(str)) return '"' + str.replace(/"/g, '""') + '"';
  return str;
};

const downloadFile = (filename, content, mime) => {
  if (typeof window === 'undefined') return;
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const HISTORY_PERIOD_OPTIONS = [
  { id: 'today', label: "Aujourd'hui" },
  { id: '7d', label: '7 derniers jours' },
  { id: '30d', label: '30 derniers jours' },
  { id: 'month', label: 'Mois en cours' },
  { id: 'all', label: 'Tout' },
  { id: 'custom', label: 'Période personnalisée' }
];

const getPeriodRange = (periodId, customFrom, customTo) => {
  const now = new Date();
  if (periodId === 'today') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return { from: start, to: start + 24 * 3600 * 1000 - 1 };
  }
  if (periodId === '7d') return { from: now.getTime() - 7 * 24 * 3600 * 1000, to: now.getTime() };
  if (periodId === '30d') return { from: now.getTime() - 30 * 24 * 3600 * 1000, to: now.getTime() };
  if (periodId === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    return { from: start, to: now.getTime() };
  }
  if (periodId === 'custom') {
    const from = customFrom ? new Date(customFrom + 'T00:00:00').getTime() : 0;
    const to = customTo ? new Date(customTo + 'T23:59:59').getTime() : Date.now();
    return { from, to };
  }
  return { from: 0, to: Date.now() };
};

const getOrderPricing = (order = {}) => {
  const pricing = order.pricing || {};
  const itemSubtotal = (order.items || []).reduce((total, item) => total + Number(item.price || item.customerPrice || 0) * Number(item.qty || 1), 0);
  const itemRestaurantSubtotal = (order.items || []).reduce((total, item) => total + Number(item.restaurantPrice || item.basePrice || item.originalPrice || item.price || 0) * Number(item.qty || 1), 0);
  const subtotal = Number(pricing.subtotal ?? order.subtotal ?? itemSubtotal);
  const restaurantSubtotal = Number(pricing.restaurantSubtotal ?? pricing.restaurantNet ?? itemRestaurantSubtotal);
  const deliveryFee = Number(pricing.deliveryFee ?? order.deliveryFee ?? 0);
  const total = Number(pricing.total ?? order.total ?? order.amount ?? (subtotal + deliveryFee));
  const platformNet = Number(pricing.platformNet ?? pricing.platformFee ?? Math.max(0, subtotal - restaurantSubtotal));
  const restaurantNet = Number(pricing.restaurantNet ?? restaurantSubtotal);
  return { subtotal, restaurantSubtotal, deliveryFee, total, platformNet, restaurantNet, commissionRate: Number(pricing.commissionRate || 0) };
};

// Reprend le même format que le récapitulatif client (EatCartScreen mobile)
// pour que les options choisies (taille, sauce, suppléments...) restent lisibles côté cuisine.
const formatSelectedOptions = (item = {}) => {
  if (Array.isArray(item.selectedOptionDetails) && item.selectedOptionDetails.length) {
    return item.selectedOptionDetails.flatMap(group => {
      const groupTitle = group.groupTitle || group.title || 'Option';
      const choices = Array.isArray(group.choices) ? group.choices : [];
      return choices.map(choice => {
        const label = choice.label || choice.name || choice.value || 'Option';
        const price = Number(choice.price || 0);
        return `${groupTitle}: ${label}${price > 0 ? ` (+${price.toFixed(2)} EUR)` : ''}`;
      });
    });
  }
  if (item.selectedOptions && Object.keys(item.selectedOptions).length) {
    return Object.keys(item.selectedOptions).map(label => {
      const value = item.selectedOptions[label];
      const text = Array.isArray(value) ? value.join(', ') : value;
      return `${label}: ${text}`;
    });
  }
  return item.selectedDrink ? [`Boisson: ${item.selectedDrink}`] : [];
};

const statusLabels = {
  PLACED: 'Nouvelle',
  PAID: 'Payee',
  RESTAURANT_ACCEPTED: 'Acceptee',
  PREPARING: 'Preparation',
  READY_FOR_PICKUP: 'Prete retrait',
  PICKED_UP: 'En livraison',
  DELIVERED: 'Livree',
  CANCELLED: 'Annulee'
};

const makeId = (prefix, value) => {
  const cleanValue = String(value || prefix).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${prefix}-${cleanValue || Date.now()}`;
};

// Convertit une saisie utilisateur ("12,50", "12.5", "", "abc") en nombre sûr pour Firebase.
// Empeche les NaN qui font echouer update() avec "values argument contains NaN".
const sanitizePrice = (input) => {
  if (input === null || input === undefined) return 0;
  const normalized = String(input).replace(',', '.').replace(/[^0-9.-]/g, '');
  const value = parseFloat(normalized);
  return Number.isFinite(value) ? value : 0;
};

const cleanFileName = (fileName = 'image') => fileName.toLowerCase().replace(/[^a-z0-9.]+/g, '-').replace(/^-|-$/g, '');

const uploadEatMedia = async (storage, pathPrefix, file, onProgress, documentMode = false) => {
  if (!storage || !file) throw new Error('Storage indisponible');
  const isImage = Boolean(file.type && file.type.startsWith('image/'));
  const isPdf = file.type === 'application/pdf';
  if (!isImage && (!documentMode || !isPdf)) throw new Error(documentMode ? 'Choisissez une image JPG, PNG, WebP ou un PDF.' : 'Choisissez une image JPG, PNG ou WebP.');
  if (file.size > 8 * 1024 * 1024) throw new Error(documentMode ? 'Document trop lourd. Taille maximum: 8 Mo.' : 'Image trop lourde. Taille maximum: 8 Mo.');
  const fileRef = storageRef(storage, `${pathPrefix}/${Date.now()}-${cleanFileName(file.name)}`);
  const task = uploadBytesResumable(fileRef, file, { contentType: file.type || 'image/jpeg' });
  return new Promise((resolve, reject) => {
    task.on('state_changed', snapshot => {
      const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
      if (onProgress) onProgress(progress);
    }, reject, async () => {
      try {
        resolve(await getDownloadURL(task.snapshot.ref));
      } catch (error) {
        reject(error);
      }
    });
  });
};

// Détection portrait/paysage en direct (tablette en cuisine posée puis
// tournée en cours de service). Piloter la mise en page par cette valeur en
// plus des breakpoints de largeur : un simple media-query de largeur ne
// suffit pas, deux tablettes de même largeur en portrait vs paysage n'ont
// pas la même place réelle disponible.
const useIsLandscape = () => {
  const getValue = () => {
    if (typeof window === 'undefined') return true;
    if (window.matchMedia) return window.matchMedia('(orientation: landscape)').matches;
    return window.innerWidth > window.innerHeight;
  };
  const [isLandscape, setIsLandscape] = useState(getValue);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(orientation: landscape)');
    const update = () => setIsLandscape(getValue());
    if (mql.addEventListener) mql.addEventListener('change', update);
    else if (mql.addListener) mql.addListener(update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', update);
      else if (mql.removeListener) mql.removeListener(update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);
  return isLandscape;
};

// Detection plateforme pour des instructions d'installation PWA exactes.
// navigator.platform+maxTouchPoints est necessaire car iPadOS (13+) usurpe
// le user-agent macOS par defaut : sans ce test, un iPad recevait les
// instructions Chrome au lieu de Safari "Partager > Sur l'écran d'accueil".
const detectPwaPlatform = () => {
  if (typeof window === 'undefined') return { os: 'other', isFirefox: false };
  const ua = window.navigator.userAgent || '';
  const isIPadOS = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
  const isFirefox = /firefox|fxios/i.test(ua);
  let os = 'other';
  if (/iphone|ipod/i.test(ua) || isIPadOS) os = 'ios';
  else if (/macintosh/i.test(ua)) os = 'mac';
  else if (/windows/i.test(ua)) os = 'windows';
  else if (/android/i.test(ua)) os = 'android';
  return { os, isFirefox };
};

export default function RestaurantPortal() {
  const dispatch = useDispatch();
  const firebase = useContext(FirebaseContext);
  const storage = firebase && firebase.storage;
  const profile = useSelector(state => state.auth.profile || {});
  const allOrders = useSelector(state => state.eatdata.orders || []);
  const eatError = useSelector(state => state.eatdata.error);
  const restaurants = useSelector(state => state.eatdata.restaurants || []);
  const eatSettings = useSelector(state => state.eatdata.settings || {});
  const menus = useSelector(state => state.eatdata.menus || {});
  const [tab, setTab] = useState(0);
  const [itemDraft, setItemDraft] = useState({ name: '', description: '', price: '', categoryId: '', imageUrl: '', drinkOptionsText: '', optionGroupsText: '', allergenIds: [] });
  const [categoryDraft, setCategoryDraft] = useState({ presetId: 'plats', name: '' });
  const [cancelOrder, setCancelOrder] = useState(null);
  const [cancelReason, setCancelReason] = useState(cancelReasons[0]);
  const [zoomOrder, setZoomOrder] = useState(null);
  const [chatOrder, setChatOrder] = useState(null);
  const [restaurantNotifications, setRestaurantNotifications] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('');
  const [menuSaveState, setMenuSaveState] = useState({});
  const [menuFormState, setMenuFormState] = useState({ status: '', message: '' });
  // La sonnerie doit fonctionner par défaut (les restaurateurs ne pensent pas
  // toujours à cliquer sur "Activer alertes") : ON tant que l'utilisateur n'a
  // pas explicitement choisi "off" une fois sur cette tablette.
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      const stored = window.localStorage.getItem('restaurantPortalSound');
      return stored === null ? true : stored === 'on';
    } catch (error) { return true; }
  });
  const [notificationPermission, setNotificationPermission] = useState(getNotificationPermission);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [iosInstallOpen, setIosInstallOpen] = useState(false);
  const [liveToast, setLiveToast] = useState('');
  const [errorToast, setErrorToast] = useState('');
  const isInstalled = typeof window !== 'undefined' && (window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches);
  const pwaPlatform = useMemo(detectPwaPlatform, []);
  const isIOS = pwaPlatform.os === 'ios';
  const isLandscape = useIsLandscape();
  const [historyPeriod, setHistoryPeriod] = useState('30d');
  const [historyCustomFrom, setHistoryCustomFrom] = useState('');
  const [historyCustomTo, setHistoryCustomTo] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState('ALL');
  const [historySortOrder, setHistorySortOrder] = useState('desc');
  const [historySearch, setHistorySearch] = useState('');
  const [visualFlash, setVisualFlash] = useState(false);
  const knownNewOrderIdsRef = useRef(new Set());
  const ordersBootstrappedRef = useRef(false);
  const audioContextRef = useRef(null);

  useEffect(() => {
    dispatch(api.fetchEatRestaurants());
    dispatch(api.fetchEatSettings());
  }, [dispatch]);

  // Les navigateurs bloquent l'audio tant qu'aucun geste utilisateur n'a eu
  // lieu sur la page (politique autoplay). Comme la sonnerie doit se
  // déclencher automatiquement à l'arrivée d'une commande (sans clic sur
  // "Activer alertes"), on "débloque" l'AudioContext dès le tout premier
  // clic/appui sur l'écran, pour que la sonnerie automatique soit ensuite
  // réellement audible.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return undefined;
    const unlockAudio = () => {
      try {
        const audioContext = audioContextRef.current || new AudioContextCtor();
        audioContextRef.current = audioContext;
        if (audioContext.state === 'suspended') audioContext.resume().catch(() => {});
      } catch (error) {}
    };
    window.addEventListener('pointerdown', unlockAudio, { once: true });
    window.addEventListener('keydown', unlockAudio, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleBeforeInstallPrompt = event => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const adminRestaurants = useMemo(() => {
    return [...restaurants]
      .filter(item => !item.archived && item.deletedAt === undefined)
      .sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')));
  }, [restaurants]);

  useEffect(() => {
    if (profile.usertype !== 'admin' || selectedRestaurantId || !adminRestaurants.length) return;
    setSelectedRestaurantId(adminRestaurants[0].id);
  }, [adminRestaurants, profile.usertype, selectedRestaurantId]);

  const restaurantId = useMemo(() => {
    if (profile.restaurantId) return profile.restaurantId;
    if (profile.serviceCapabilities && profile.serviceCapabilities.restaurantId) return profile.serviceCapabilities.restaurantId;
    if (profile.usertype === 'admin') return selectedRestaurantId || (adminRestaurants[0] && adminRestaurants[0].id) || null;
    // Fallback: find restaurant where ownerUid matches current user uid
    if (profile.uid && restaurants.length) {
      const owned = restaurants.find(r => r.ownerUid === profile.uid || r.ownerId === profile.uid);
      if (owned) return owned.id;
    }
    return null;
  }, [adminRestaurants, profile, restaurants, selectedRestaurantId]);

  const restaurant = useMemo(() => {
    return restaurants.find(item => item.id === restaurantId) || null;
  }, [restaurantId, restaurants]);

  // Toujours filtrer les commandes pour ne montrer QUE celles du restaurant connecté.
  // Empêche tout débordement si le store contient encore des commandes d'autres restos.
  const orders = useMemo(() => {
    if (!restaurantId) return [];
    return allOrders.filter(order => order && order.restaurantId === restaurantId);
  }, [allOrders, restaurantId]);

  useEffect(() => {
    if (restaurantId) {
      dispatch(api.fetchEatOrders({ restaurantId }));
      dispatch(api.fetchEatMenu(restaurantId));
    }
  }, [dispatch, restaurantId]);

  useEffect(() => {
    if (!firebase || !firebase.database || !restaurantId) {
      setRestaurantNotifications([]);
      return undefined;
    }
    const notificationsRef = databaseRef(firebase.database, `eat/restaurantNotifications/${restaurantId}`);
    return onValue(notificationsRef, snapshot => {
      const data = snapshot.val() || {};
      const notifications = Object.keys(data).map(id => ({ id, ...data[id] })).sort((left, right) => (right.createdAt || 0) - (left.createdAt || 0));
      setRestaurantNotifications(notifications);
    });
  }, [firebase, restaurantId]);

  const menu = menus[restaurantId] || { categories: {}, items: {} };
  const categories = Object.values(menu.categories || {}).sort((left, right) => (left.sort || 0) - (right.sort || 0));
  const items = Object.values(menu.items || {}).sort((left, right) => (left.sort || 0) - (right.sort || 0));
  const categoryLookup = menu.categories || {};
  const menuSections = MENU_CATEGORY_PRESETS.map(section => ({
    ...section,
    items: items.filter(item => menuSectionId(item, categoryLookup) === section.id)
  }));
  const storedAccent = BRAND_ACCENTS.find(item => item.color === (restaurant && restaurant.brandColor));
  const accent = storedAccent || BRAND_ACCENTS[0];
  const commissionRate = getCommissionRate(eatSettings, restaurant || {});
  const commissionPercent = Math.round(commissionRate * 1000) / 10;
  const itemPreviewPrice = getClientItemPrice({ restaurantPrice: itemDraft.price || 0 }, commissionRate);
  const coverImageUrl = restaurant && restaurant.coverImageUrl;
  const restaurantDocsComplete = hasRequiredRestaurantDocs(restaurant || {});
  const activeOrders = orders.filter(order => !['DELIVERED', 'CANCELLED'].includes(order.status));
  const completedOrders = orders.filter(order => ['DELIVERED', 'CANCELLED'].includes(order.status));
  const historyRange = useMemo(() => getPeriodRange(historyPeriod, historyCustomFrom, historyCustomTo), [historyPeriod, historyCustomFrom, historyCustomTo]);
  const filteredHistory = useMemo(() => {
    const search = historySearch.trim().toLowerCase();
    return completedOrders
      .filter(order => {
        const ts = getOrderTimestamp(order);
        if (historyPeriod !== 'all' && (ts < historyRange.from || ts > historyRange.to)) return false;
        if (historyStatusFilter !== 'ALL' && order.status !== historyStatusFilter) return false;
        if (search) {
          const haystack = `${order.reference || ''} ${order.id || ''} ${order.customerName || ''} ${order.customerPhone || ''}`.toLowerCase();
          if (!haystack.includes(search)) return false;
        }
        return true;
      })
      .sort((a, b) => historySortOrder === 'desc' ? getOrderTimestamp(b) - getOrderTimestamp(a) : getOrderTimestamp(a) - getOrderTimestamp(b));
  }, [completedOrders, historyPeriod, historyRange, historyStatusFilter, historySortOrder, historySearch]);
  const historySummary = useMemo(() => {
    const delivered = filteredHistory.filter(order => order.status === 'DELIVERED');
    const cancelled = filteredHistory.filter(order => order.status === 'CANCELLED');
    const grossRevenue = delivered.reduce((total, order) => total + getOrderPricing(order).total, 0);
    const netRevenue = delivered.reduce((total, order) => total + getOrderPricing(order).restaurantNet, 0);
    return { delivered: delivered.length, cancelled: cancelled.length, grossRevenue, netRevenue, total: filteredHistory.length };
  }, [filteredHistory]);
  const revenue = orders.filter(order => order.status !== 'CANCELLED').reduce((total, order) => total + getOrderPricing(order).restaurantNet, 0);
  const nextOrders = activeOrders.filter(order => ['PLACED', 'PAID'].includes(order.status));
  const unreadNotifications = restaurantNotifications.filter(item => !item.read);
  const topProducts = useMemo(() => {
    const totals = orders
      .filter(order => !['CANCELLED', 'PAYMENT_PENDING'].includes(order.status))
      .reduce((values, order) => {
        (order.items || []).forEach(item => {
          const itemId = item.id || item.itemId || item.name;
          if (!itemId) return;
          const qty = Number(item.qty || item.quantity || 1);
          const current = values[itemId] || { id: itemId, name: item.name || itemId, qty: 0, revenue: 0 };
          current.qty += qty;
          current.revenue += Number(item.price || item.customerPrice || 0) * qty;
          values[itemId] = current;
        });
        return values;
      }, {});
    return Object.values(totals)
      .sort((left, right) => right.qty - left.qty || right.revenue - left.revenue)
      .slice(0, 3)
      .map((item, index) => ({ ...item, rank: index + 1 }));
  }, [orders]);
  const topProductRankById = useMemo(() => topProducts.reduce((values, item) => ({ ...values, [item.id]: item.rank }), {}), [topProducts]);

  const playOrderBell = useCallback((force = false) => {
    if ((!soundEnabled && !force) || typeof window === 'undefined') return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const audioContext = audioContextRef.current || new AudioContext();
    audioContextRef.current = audioContext;
    if (audioContext.state === 'suspended') audioContext.resume().catch(() => {});
    // Sonnerie cuisine "restaurant" : double salve forte, 2 séries de 4 chimes alternées
    const baseSeries = [
      { offset: 0.00, freq: 1320, dur: 0.30 },
      { offset: 0.18, freq: 1760, dur: 0.30 },
      { offset: 0.40, freq: 1320, dur: 0.30 },
      { offset: 0.62, freq: 1976, dur: 0.55 }
    ];
    const pattern = [...baseSeries, ...baseSeries.map(p => ({ ...p, offset: p.offset + 1.30 }))];
    pattern.forEach(({ offset, freq, dur }) => {
      const startAt = audioContext.currentTime + offset;
      // Mix 2 oscillators (square + triangle) for richer & louder timbre
      const oscillator = audioContext.createOscillator();
      const oscillator2 = audioContext.createOscillator();
      const gain = audioContext.createGain();
      oscillator.type = 'square';
      oscillator.frequency.value = freq;
      oscillator2.type = 'triangle';
      oscillator2.frequency.value = freq * 2;
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(0.95, startAt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
      oscillator.connect(gain);
      oscillator2.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start(startAt);
      oscillator2.start(startAt);
      oscillator.stop(startAt + dur + 0.05);
      oscillator2.stop(startAt + dur + 0.05);
    });
    // Vibrate on supported devices (tablette Android)
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try { navigator.vibrate([200, 80, 200, 80, 400]); } catch (_) {}
    }
    // Flash visuel rouge plein écran
    setVisualFlash(true);
    setTimeout(() => setVisualFlash(false), 2500);
  }, [soundEnabled]);

  const showBrowserNotification = useCallback((order) => {
    if (typeof window === 'undefined' || !('Notification' in window) || window.Notification.permission !== 'granted') return;
    try {
      const itemCount = (order.items || []).reduce((total, item) => total + Number(item.qty || 1), 0);
      const notification = new window.Notification('Nouvelle commande Allô Driver Eats', {
        body: `${order.reference || order.id || 'Commande'} - ${itemCount || (order.items || []).length} article(s)`,
        tag: `eat-order-${order.id || order.reference}`,
        renotify: true,
        requireInteraction: true
      });
      notification.onclick = () => {
        window.focus();
        setTab(0);
        notification.close();
      };
    } catch (error) {}
  }, []);

  useEffect(() => {
    const currentNewOrders = nextOrders.filter(isNewRestaurantOrder);
    const currentIds = new Set(currentNewOrders.map(order => order.id || order.reference).filter(Boolean));
    if (!ordersBootstrappedRef.current) {
      knownNewOrderIdsRef.current = currentIds;
      ordersBootstrappedRef.current = true;
      return;
    }
    const addedOrders = currentNewOrders.filter(order => {
      const orderId = order.id || order.reference;
      return orderId && !knownNewOrderIdsRef.current.has(orderId);
    });
    knownNewOrderIdsRef.current = currentIds;
    if (!addedOrders.length) return;
    const firstOrder = addedOrders[0];
    setTab(0);
    setLiveToast(`${addedOrders.length} nouvelle commande à traiter`);
    playOrderBell(true);
    showBrowserNotification(firstOrder);
    if (typeof document !== 'undefined' && document.hidden) {
      document.title = `(${addedOrders.length}) Nouvelle commande - Allô Driver Eats`;
    }
  }, [nextOrders, playOrderBell, showBrowserNotification]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const resetTitle = () => { document.title = 'Allô Driver | Déplacez-Vous Librement !'; };
    document.addEventListener('visibilitychange', resetTitle);
    return () => document.removeEventListener('visibilitychange', resetTitle);
  }, []);

  // Sonnerie répétée tant qu'au moins une commande non acceptée attend en cuisine
  useEffect(() => {
    if (!soundEnabled) return undefined;
    const pendingCount = nextOrders.filter(isNewRestaurantOrder).length;
    if (pendingCount === 0) return undefined;
    const interval = setInterval(() => {
      playOrderBell(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [soundEnabled, nextOrders, playOrderBell]);

  // Les actions cuisine (Preparation/Prete retrait) sont bloquees en silence
  // tant qu'aucun livreur n'a accepte la course (voir updateEatOrderStatus) :
  // sans ca, cliquer sur le bouton ne "faisait rien" a l'oeil du restaurateur.
  useEffect(() => {
    if (!eatError) return;
    setErrorToast(eatError);
    dispatch(api.clearEatMessages());
  }, [eatError, dispatch]);

  const enableRestaurantAlerts = async () => {
    setSoundEnabled(true);
    try { window.localStorage.setItem('restaurantPortalSound', 'on'); } catch (error) {}
    playOrderBell();
    if (typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'default') {
      const permission = await window.Notification.requestPermission();
      setNotificationPermission(permission);
    } else {
      setNotificationPermission(getNotificationPermission());
    }
    setLiveToast('Alertes restaurateur activées sur cette tablette');
  };

  const disableRestaurantAlerts = () => {
    setSoundEnabled(false);
    try { window.localStorage.setItem('restaurantPortalSound', 'off'); } catch (error) {}
    setLiveToast('Sonnerie désactivée');
  };

  const installPwa = async () => {
    if (isInstalled) return;
    if (isIOS) { setIosInstallOpen(true); return; }
    if (!installPrompt) { setIosInstallOpen(true); return; }
    installPrompt.prompt();
    await installPrompt.userChoice.catch(() => null);
    setInstallPrompt(null);
  };

  const updateRestaurant = (patch) => {
    if (restaurantId) dispatch(api.updateEatRestaurant(restaurantId, patch));
  };

  // Met a jour l'adresse + geolocalise automatiquement (lat/lng/restaurantPoint/coords/location.lat,lng).
  // Empeche d'avoir a corriger manuellement la base apres un changement d'adresse.
  const applyAddressUpdate = async (selection) => {
    if (!restaurantId) return;
    const desc = (typeof selection === 'string') ? selection : (selection && selection.description) || '';
    let coords = selection && selection.coords ? selection.coords : null;
    if (!desc) return;
    if (!coords) {
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(desc)}&limit=1`;
        const resp = await window.fetch(url, { headers: { 'Accept-Language': 'fr,en' } });
        if (resp && resp.ok) {
          const json = await resp.json();
          if (json && json[0]) coords = { lat: parseFloat(json[0].lat), lng: parseFloat(json[0].lon) };
        }
      } catch (_) { /* ignore */ }
    }
    const patch = {
      address: desc,
      restaurantAddress: desc,
      location: { ...((restaurant && restaurant.location) || {}), add: desc, address: desc, ...(coords ? { lat: coords.lat, lng: coords.lng } : {}) }
    };
    if (coords) {
      patch.lat = coords.lat;
      patch.lng = coords.lng;
      patch.restaurantPoint = { lat: coords.lat, lng: coords.lng };
      patch.coords = { lat: coords.lat, lng: coords.lng };
    }
    updateRestaurant(patch);
  };

  const updateOrder = (order, status) => {
    dispatch(api.updateEatOrderStatus(order, status));
  };

  const openCancelDialog = (order) => {
    setCancelOrder(order);
    setCancelReason(cancelReasons[0]);
  };

  const confirmCancelOrder = () => {
    if (!cancelOrder) return;
    dispatch(api.updateEatOrderStatus(cancelOrder, 'CANCELLED', {
      cancellationReason: cancelReason,
      cancelledBy: 'restaurant',
      cancelledByName: restaurant && restaurant.name ? restaurant.name : 'Restaurant'
    }));
    setCancelOrder(null);
  };

  const markRestaurantNotificationsRead = () => {
    if (!firebase || !firebase.database || !restaurantId || !unreadNotifications.length) return;
    const updates = unreadNotifications.reduce((values, item) => {
      values[`eat/restaurantNotifications/${restaurantId}/${item.id}/read`] = true;
      return values;
    }, {});
    updateDatabase(databaseRef(firebase.database), updates);
  };

  const saveCategory = async () => {
    const preset = MENU_CATEGORY_PRESETS.find(item => item.id === categoryDraft.presetId);
    const name = categoryDraft.name.trim() || (preset && preset.name) || '';
    if (!restaurantId || !name) return;
    const categoryId = preset ? preset.id : makeId('cat', name);
    setMenuFormState({ status: 'saving', message: 'Enregistrement de la catégorie...' });
    try {
      await dispatch(api.updateEatMenu(restaurantId, {
        [`categories/${categoryId}`]: { id: categoryId, name, cuisineId: preset && preset.cuisineId, sort: categories.length + 1, active: true }
      }));
      setCategoryDraft({ presetId: 'plats', name: '' });
      setMenuFormState({ status: 'saved', message: 'Catégorie enregistrée.' });
    } catch (error) {
      setMenuFormState({ status: 'error', message: error && error.message ? error.message : 'Catégorie non enregistrée.' });
    }
  };

  const saveItem = async () => {
    const name = itemDraft.name.trim();
    if (!restaurantId || !name) return;
    const itemId = `${makeId('item', name)}-${Date.now().toString(36)}`;
    const categoryId = itemDraft.categoryId || 'plats';
    const nextCategories = categories.length ? menu.categories : MENU_CATEGORY_PRESETS.reduce((values, category, index) => {
      values[category.id] = { id: category.id, name: category.name, cuisineId: category.cuisineId, sort: index + 1, active: true };
      return values;
    }, {});
    const safePrice = sanitizePrice(itemDraft.price);
    const nextItem = {
          id: itemId,
          categoryId,
          name,
          description: itemDraft.description || '',
          imageUrl: itemDraft.imageUrl || '',
          drinkOptions: parseOptionLines(itemDraft.drinkOptionsText),
          optionGroups: parseOptionGroupsText(itemDraft.optionGroupsText),
          allergens: normalizeAllergens(itemDraft.allergenIds),
          allergenIds: normalizeAllergens(itemDraft.allergenIds),
          price: safePrice,
          restaurantPrice: safePrice,
          basePrice: safePrice,
          active: true,
          sort: items.length + 1
        };
    setMenuFormState({ status: 'saving', message: 'Enregistrement du plat...' });
    try {
      await dispatch(api.updateEatMenu(restaurantId, {
        categories: nextCategories,
        [`items/${itemId}`]: nextItem
      }));
      setItemDraft({ name: '', description: '', price: '', categoryId: '', imageUrl: '', drinkOptionsText: '', optionGroupsText: '', allergenIds: [] });
      setMenuFormState({ status: 'saved', message: 'Plat enregistré.' });
    } catch (error) {
      setMenuFormState({ status: 'error', message: error && error.message ? error.message : 'Plat non enregistré.' });
    }
  };

  const updateItem = async (item, patch) => {
    if (!restaurantId || !item || !item.id) return false;
    setMenuSaveState(prev => ({ ...prev, [item.id]: { status: 'saving', message: 'Sauvegarde...' } }));
    const patchWithDate = { ...patch, updatedAt: Date.now() };
    const targetedPatch = Object.keys(patchWithDate).reduce((values, key) => {
      values[`items/${item.id}/${key}`] = patchWithDate[key];
      return values;
    }, {});
    try {
      await dispatch(api.updateEatMenu(restaurantId, targetedPatch));
      setMenuSaveState(prev => ({ ...prev, [item.id]: { status: 'saved', message: 'Sauvegardé' } }));
      return true;
    } catch (error) {
      setMenuSaveState(prev => ({ ...prev, [item.id]: { status: 'error', message: error && error.message ? error.message : 'Sauvegarde impossible' } }));
      return false;
    }
  };

  const toggleItem = (item) => {
    updateItem(item, { active: !item.active });
  };

  const updateOpeningDay = (dayKey, patch) => {
    const openingHours = getOpeningHours(restaurant || {});
    updateRestaurant({
      openingHours: {
        ...openingHours,
        [dayKey]: { ...(openingHours[dayKey] || {}), ...patch }
      }
    });
  };

  const exportHistoryCsv = (rows) => {
    const header = ['Reference', 'Date', 'Statut', 'Client', 'Telephone', 'Articles', 'Sous-total', 'Frais livraison', 'Total client EUR', 'Net restaurant EUR', 'Mode paiement', 'Adresse livraison', 'Raison annulation'];
    const lines = [header.map(toCsvCell).join(';')];
    rows.forEach(order => {
      const pricing = getOrderPricing(order);
      const ts = getOrderTimestamp(order);
      const itemsLabel = (order.items || []).map(it => `${it.qty || 1}x ${it.name || ''}`).join(' | ');
      lines.push([
        order.reference || order.id,
        formatDateTime(ts),
        statusLabels[order.status] || order.status,
        order.customerName || '',
        order.customerPhone || '',
        itemsLabel,
        pricing.subtotal.toFixed(2),
        pricing.deliveryFee.toFixed(2),
        pricing.total.toFixed(2),
        pricing.restaurantNet.toFixed(2),
        order.paymentMethod || '',
        (order.deliveryAddress && (order.deliveryAddress.address || order.deliveryAddress.add)) || '',
        order.cancellationReason || ''
      ].map(toCsvCell).join(';'));
    });
    const filename = `historique-commandes-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadFile(filename, '\uFEFF' + lines.join('\r\n'), 'text/csv;charset=utf-8;');
  };

  // Export PDF leger via window.print sur fenetre dediee : pas de dependance lourde,
  // l'utilisateur choisit "Enregistrer en PDF" dans la boite d'impression natif.
  const exportHistoryPdf = (rows, summary, restaurantInfo, periodId, range) => {
    if (typeof window === 'undefined') return;
    const periodLabel = (HISTORY_PERIOD_OPTIONS.find(p => p.id === periodId) || {}).label || periodId;
    const periodRangeLabel = periodId === 'all' ? 'Toute la periode' : `${formatDateOnly(range.from)} - ${formatDateOnly(range.to)}`;
    const restaurantName = (restaurantInfo && restaurantInfo.name) || 'Restaurant';
    const head = `<style>
      @page { size: A4; margin: 14mm; }
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #111318; margin: 0; }
      .head { display:flex; align-items:center; gap:14px; border-bottom: 3px solid #d4af37; padding-bottom: 12px; margin-bottom: 16px; }
      .head h1 { margin:0; font-size: 22px; }
      .head p { margin:2px 0 0; color:#555; font-size: 12px; }
      .summary { display:grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin: 12px 0 18px; }
      .card { background:#f6f7f9; border-radius: 8px; padding: 10px; text-align:center; }
      .card b { display:block; font-size: 16px; }
      .card span { font-size: 11px; color:#555; }
      table { width:100%; border-collapse: collapse; font-size: 11px; }
      th, td { padding: 6px 8px; border-bottom: 1px solid #eaecef; text-align: left; vertical-align: top; }
      th { background:#111318; color:#f2cc38; }
      tr:nth-child(even) td { background:#fbfaf6; }
      .badge { display:inline-block; padding:2px 6px; border-radius: 4px; font-size: 10px; font-weight: 700; }
      .badge.ok { background:#faf3d9; color:#8a6d16; }
      .badge.ko { background:#ffebee; color:#b71c1c; }
      .right { text-align:right; }
      .foot { margin-top: 14px; font-size: 10px; color:#888; }
    </style>`;
    const summaryHtml = `<div class="summary">
      <div class="card"><b>${summary.total}</b><span>commande(s)</span></div>
      <div class="card"><b>${summary.delivered}</b><span>livrée(s)</span></div>
      <div class="card"><b>${summary.cancelled}</b><span>annulée(s)</span></div>
      <div class="card"><b>${currency(summary.grossRevenue)}</b><span>CA brut</span></div>
      <div class="card"><b>${currency(summary.netRevenue)}</b><span>Net restaurant</span></div>
    </div>`;
    const escapeHtml = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    const rowsHtml = rows.map(order => {
      const pricing = getOrderPricing(order);
      const ts = getOrderTimestamp(order);
      const itemsLabel = (order.items || []).map(it => `${it.qty || 1}x ${escapeHtml(it.name || '')}`).join('<br/>');
      return `<tr>
        <td>${escapeHtml(order.reference || order.id)}</td>
        <td>${formatDateTime(ts)}</td>
        <td><span class="badge ${order.status === 'CANCELLED' ? 'ko' : 'ok'}">${statusLabels[order.status] || order.status}</span></td>
        <td>${escapeHtml(order.customerName || 'Client')}</td>
        <td>${itemsLabel}</td>
        <td class="right">${currency(pricing.total)}</td>
        <td class="right">${currency(pricing.restaurantNet)}</td>
      </tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Historique ${escapeHtml(restaurantName)}</title>${head}</head><body>
      <div class="head">
        <img src="${eatsLogo}" alt="Allô Driver Eats" style="height:48px"/>
        <div>
          <h1>Historique commandes — ${escapeHtml(restaurantName)}</h1>
          <p>${escapeHtml(periodLabel)} · ${escapeHtml(periodRangeLabel)} · Édité le ${formatDateTime(Date.now())}</p>
        </div>
      </div>
      ${summaryHtml}
      <table>
        <thead><tr><th>Réf.</th><th>Date</th><th>Statut</th><th>Client</th><th>Articles</th><th class="right">Total</th><th class="right">Net resto</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <p class="foot">Allô Driver Eats — document généré depuis l'espace restaurateur.</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) { setLiveToast('Autorisez les pop-ups pour exporter en PDF.'); return; }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <Box sx={styles.page}>
      {visualFlash ? (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1500, pointerEvents: 'none', backgroundColor: 'rgba(239, 68, 68, 0.32)', animation: 'flashAlert 0.4s ease-in-out 0s 6 alternate', '@keyframes flashAlert': { '0%': { backgroundColor: 'rgba(239, 68, 68, 0.0)' }, '100%': { backgroundColor: 'rgba(239, 68, 68, 0.45)' } } }} />
      ) : null}
      {restaurant && (
        <Box sx={{ ...styles.brandBanner, borderColor: accent.color }}>
          <Box sx={styles.coverPreview}>
            {coverImageUrl ? <img src={coverImageUrl} alt="" style={styles.coverImage} /> : <EatHeaderMotif restaurant={restaurant} />}
          </Box>
          <Box sx={styles.bannerShade} />
          <Box sx={{ ...styles.logoPreview, borderColor: accent.color }}>
            {restaurant.logoUrl ? <img src={restaurant.logoUrl} alt="" style={styles.logoImage} /> : <img src={eatsLogo} alt="Allô Driver Eats" style={styles.logoImage} />}
          </Box>
          <Box sx={styles.bannerContent}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Chip size="small" sx={styles.bannerChip} icon={<StorefrontIcon />} label={restaurant.active !== false && restaurantDocsComplete ? 'Restaurant publié' : 'Restaurant masqué'} />
              <Chip size="small" sx={styles.bannerChip} icon={<ImageIcon />} label={restaurant.coverImageUrl ? 'Vitrine personnalisée' : 'Motif Allô'} />
              <Chip size="small" sx={styles.bannerChip} icon={<ReceiptLongIcon />} label={restaurantDocsComplete ? 'K-bis/RIB OK' : 'K-bis/RIB requis'} />
            </Stack>
            <Typography sx={styles.bannerTitle}>{restaurant.name}</Typography>
            <Typography sx={styles.bannerSubtitle}>{restaurant.specialty || cuisineLabel(restaurant.cuisine)}</Typography>
          </Box>
        </Box>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={styles.header}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h4" sx={styles.title}>{restaurant ? restaurant.name : 'Espace restaurateur'}</Typography>
            <Chip size="small" sx={GOLD_CHIP_SX} label="App tablette" />
            {restaurant && restaurant.rating ? <Chip size="small" label={`Note ${Number(restaurant.rating).toFixed(1)}/5${restaurant.ratingCount ? ` - ${restaurant.ratingCount} avis` : ''}`} /> : null}
          </Stack>
          <Typography color="text.secondary" noWrap>{restaurant ? `${cuisineLabel(restaurant.cuisine)} - ${getRestaurantAddress(restaurant) || 'Adresse non renseignee'}` : 'Restaurant a lier'}</Typography>
          {restaurant ? <Typography color="text.secondary" variant="body2" noWrap>{getOpeningSummary(restaurant)}</Typography> : null}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {profile.usertype === 'admin' && adminRestaurants.length ? (
            <TextField
              select
              size="small"
              label="Restaurant"
              value={restaurantId || ''}
              onChange={event => setSelectedRestaurantId(event.target.value)}
              SelectProps={{ native: true }}
              sx={{ minWidth: { xs: '100%', sm: 260 } }}
            >
              {adminRestaurants.map(item => <option key={item.id} value={item.id}>{item.name || item.id}</option>)}
            </TextField>
          ) : null}
          <Chip color={restaurant && restaurant.active !== false && restaurantDocsComplete ? undefined : 'default'} sx={restaurant && restaurant.active !== false && restaurantDocsComplete ? GOLD_CHIP_SX : undefined} label={restaurant && restaurant.active !== false && restaurantDocsComplete ? 'Actif' : 'Inactif'} />
          <Chip color={restaurant && restaurant.isOpen ? undefined : 'warning'} sx={restaurant && restaurant.isOpen ? GOLD_CHIP_SX : undefined} label={restaurant && restaurant.isOpen ? 'Ouvert' : 'Ferme'} />
          <Button variant="contained" onClick={() => updateRestaurant({ isOpen: !(restaurant && restaurant.isOpen) })} disabled={!restaurantId}>Boutique</Button>
          <Button variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={() => dispatch(api.signOff())}>Déconnexion</Button>
        </Stack>
      </Stack>

      <Paper sx={styles.liveOpsPanel}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={styles.liveOpsIcon}><TabletMacIcon /></Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={styles.liveOpsTitle}>Console tablette temps réel</Typography>
              <Typography variant="body2" sx={styles.liveOpsText}>Gardez cet écran ouvert en cuisine : les commandes arrivent en direct avec sonnerie, notification et actions rapides.</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Chip color={nextOrders.length ? 'error' : 'success'} label={`${nextOrders.length} nouvelle(s)`} />
            <Chip color={activeOrders.length ? 'warning' : 'default'} label={`${activeOrders.length} en cours`} />
            <Chip color={soundEnabled ? 'success' : 'default'} icon={<VolumeUpIcon />} label={soundEnabled ? 'Sonnerie ON' : 'Sonnerie OFF'} />
            <Chip color={notificationPermission === 'granted' ? 'success' : notificationPermission === 'denied' ? 'error' : 'default'} label={notificationPermission === 'granted' ? 'Notifications ON' : notificationPermission === 'denied' ? 'Notifications bloquées' : 'Notifications à activer'} />
            {soundEnabled ? <Button variant="outlined" onClick={disableRestaurantAlerts}>Couper sonnerie</Button> : <Button variant="contained" onClick={enableRestaurantAlerts}>Activer alertes</Button>}
            <Button variant="outlined" startIcon={<VolumeUpIcon />} onClick={() => playOrderBell(true)}>Tester sonnerie</Button>
            {isInstalled
              ? <Button variant="outlined" disabled>✓ Appli installée</Button>
              : <Button variant="outlined" onClick={installPwa} startIcon={<TabletMacIcon />}>Installer sur cet écran</Button>
            }
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Metric icon={<ReceiptLongIcon />} label="A traiter" value={nextOrders.length} /></Grid>
        <Grid item xs={6} md={3}><Metric icon={<AccessTimeIcon />} label="En cours" value={activeOrders.length} /></Grid>
        <Grid item xs={6} md={3}><Metric icon={<MenuBookIcon />} label="Articles" value={items.length} /></Grid>
        <Grid item xs={6} md={3}><Metric icon={<LocalDiningIcon />} label="CA restaurant" value={currency(revenue)} /></Grid>
      </Grid>

      {restaurantNotifications.length ? (
        <Paper sx={styles.notificationPanel}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Box sx={styles.notificationIcon}><NotificationsActiveIcon /></Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={styles.sectionTitle}>Alertes commandes</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>{restaurantNotifications[0].msg}</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {unreadNotifications.length ? <Chip color="warning" label={`${unreadNotifications.length} nouvelle(s)`} /> : <Chip label="Tout lu" />}
              <Button size="small" variant="outlined" onClick={markRestaurantNotificationsRead} disabled={!unreadNotifications.length}>Marquer lu</Button>
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      <Paper sx={styles.workspace}>
        <Tabs value={tab} onChange={(event, nextTab) => setTab(nextTab)} variant="scrollable" scrollButtons="auto" sx={styles.tabs}>
          <Tab label="Commandes actives" />
          <Tab label="Historique" />
          <Tab label="Menu" />
          <Tab label="Boutique" />
        </Tabs>

        {tab === 0 && (
          <Box sx={styles.panel}>
            {activeOrders.length ? activeOrders.map(order => (
              <Box key={order.id} sx={isNewRestaurantOrder(order) ? styles.newOrderCard : styles.orderCard}>
                <Box sx={{ ...styles.orderHeaderBar, flexDirection: isLandscape ? { xs: 'column', md: 'row' } : 'column' }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={styles.orderHeaderTitle}>{order.reference || order.id}</Typography>
                    <Chip size="small" color={isNewRestaurantOrder(order) ? 'error' : 'default'} label={statusLabels[order.status] || order.status || 'Nouvelle'} />
                  </Stack>
                  <Typography variant="body2" sx={styles.orderHeaderMeta} noWrap>{order.customerName || order.customerId || 'Client'} - {(order.items || []).length} article(s) - Net restaurant {currency(getOrderPricing(order).restaurantNet)} - Total client {currency(getOrderPricing(order).total)}</Typography>
                </Box>
                <Stack spacing={1} sx={{ ...styles.kitchenActionsWrap, width: isLandscape ? { xs: '100%', md: 'auto' } : '100%' }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={styles.kitchenActions}>
                    {['PLACED', 'PAID'].includes(order.status) && <Button size="large" variant="contained" sx={{ bgcolor: '#d4af37', color: '#000', '&:hover': { bgcolor: '#b8941f' } }} startIcon={<RoomServiceIcon />} onClick={() => updateOrder(order, 'RESTAURANT_ACCEPTED')}>Accepter</Button>}
                    {['RESTAURANT_ACCEPTED'].includes(order.status) && <Button size="large" variant="outlined" sx={styles.headerOutlinedButton} onClick={() => updateOrder(order, 'PREPARING')}>Preparation</Button>}
                    {['RESTAURANT_ACCEPTED', 'PREPARING'].includes(order.status) && <Button size="large" variant="contained" sx={{ bgcolor: '#fff', color: '#111318', '&:hover': { bgcolor: '#e6e6e6' } }} onClick={() => updateOrder(order, 'READY_FOR_PICKUP')}>Prete retrait</Button>}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={styles.kitchenActionsSecondary}>
                    <Button fullWidth size="large" variant="outlined" sx={styles.headerOutlinedButton} startIcon={<ZoomInIcon />} onClick={() => setZoomOrder(order)}>Détail commande</Button>
                    {['PLACED', 'PAID', 'RESTAURANT_ACCEPTED', 'PREPARING'].includes(order.status) && <Button fullWidth size="large" variant="outlined" color="error" sx={{ bgcolor: 'rgba(239,68,68,0.08)' }} onClick={() => openCancelDialog(order)}>Annuler</Button>}
                  </Stack>
                  {order.courierId ? (
                    <Stack direction="row" spacing={1} sx={styles.kitchenActionsSecondary}>
                      <Button fullWidth size="large" variant="outlined" sx={styles.headerOutlinedButton} disabled={!order.courierPhone} startIcon={<PhoneIcon />} component={order.courierPhone ? 'a' : 'button'} href={order.courierPhone ? `tel:${order.courierPhone}` : undefined}>Appeler le livreur</Button>
                      <Button fullWidth size="large" variant="outlined" sx={styles.headerOutlinedButton} startIcon={<ChatIcon />} onClick={() => setChatOrder(order)}>Chat avec le livreur</Button>
                    </Stack>
                  ) : null}
                </Stack>
                </Box>
                {order.pickupCode && !['DELIVERED', 'CANCELLED'].includes(order.status) ? (
                  <Box sx={{ mt: 1.5, mb: 0.5, p: 1.5, borderRadius: 2, border: '2px solid #b8941f', bgcolor: '#faf3d9', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: '#d4af37', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>#</Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#8a6d16' }}>Code de retrait livreur</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {['PICKED_UP', 'IN_TRANSIT'].includes(order.status)
                          ? 'Commande retirée - Code conservé pour référence'
                          : 'À communiquer uniquement au livreur identifié, à la remise du sac.'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 28, fontWeight: 900, letterSpacing: 4, color: '#8a6d16', fontFamily: 'monospace' }}>{order.pickupCode}</Typography>
                  </Box>
                ) : null}
                <OrderRecap order={order} />
              </Box>
            )) : <EmptyState title="Aucune commande active" text="Les nouvelles commandes apparaitront ici." />}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={styles.panel}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2.5, borderColor: '#e1d8b8', backgroundColor: '#fffdf6' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap" useFlexGap>
                <TextField
                  select
                  size="small"
                  label="Période"
                  value={historyPeriod}
                  onChange={(event) => setHistoryPeriod(event.target.value)}
                  sx={{ minWidth: 200 }}
                >
                  {HISTORY_PERIOD_OPTIONS.map(opt => <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>)}
                </TextField>
                {historyPeriod === 'custom' ? (
                  <>
                    <TextField type="date" size="small" label="Du" InputLabelProps={{ shrink: true }} value={historyCustomFrom} onChange={(event) => setHistoryCustomFrom(event.target.value)} sx={{ minWidth: 160 }} />
                    <TextField type="date" size="small" label="Au" InputLabelProps={{ shrink: true }} value={historyCustomTo} onChange={(event) => setHistoryCustomTo(event.target.value)} sx={{ minWidth: 160 }} />
                  </>
                ) : null}
                <TextField
                  select
                  size="small"
                  label="Statut"
                  value={historyStatusFilter}
                  onChange={(event) => setHistoryStatusFilter(event.target.value)}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="ALL">Tous</MenuItem>
                  <MenuItem value="DELIVERED">Livrées</MenuItem>
                  <MenuItem value="CANCELLED">Annulées</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Tri"
                  value={historySortOrder}
                  onChange={(event) => setHistorySortOrder(event.target.value)}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="desc">Plus récentes</MenuItem>
                  <MenuItem value="asc">Plus anciennes</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  placeholder="Recherche réf., client..."
                  value={historySearch}
                  onChange={(event) => setHistorySearch(event.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  sx={{ minWidth: 220, flex: 1 }}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="outlined" startIcon={<TableChartIcon />} onClick={() => exportHistoryCsv(filteredHistory)} disabled={!filteredHistory.length}>CSV</Button>
                  <Button size="small" variant="contained" startIcon={<PictureAsPdfIcon />} onClick={() => exportHistoryPdf(filteredHistory, historySummary, restaurant, historyPeriod, historyRange)} disabled={!filteredHistory.length}>PDF</Button>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                <Chip variant="outlined" sx={{ borderColor: '#111318', color: '#111318' }} label={`${historySummary.total} commande(s)`} />
                <Chip sx={GOLD_CHIP_SX} label={`${historySummary.delivered} livrée(s)`} />
                <Chip color="error" variant="outlined" label={`${historySummary.cancelled} annulée(s)`} />
                <Chip color="default" label={`CA brut ${currency(historySummary.grossRevenue)}`} />
                <Chip variant="outlined" sx={{ borderColor: '#b8941f', color: '#8a6d16' }} label={`Net restaurant ${currency(historySummary.netRevenue)}`} />
              </Stack>
            </Paper>
            {filteredHistory.length ? filteredHistory.map(order => {
              const ts = getOrderTimestamp(order);
              const pricing = getOrderPricing(order);
              return (
                <Box key={order.id} sx={styles.orderCard}>
                  <Box sx={styles.orderHeaderBar}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={styles.orderHeaderTitle}>{order.reference || order.id}</Typography>
                        <Chip size="small" color={order.status === 'CANCELLED' ? 'error' : undefined} sx={order.status === 'CANCELLED' ? undefined : GOLD_CHIP_SX} label={statusLabels[order.status] || order.status} />
                        <Chip size="small" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff' }} label={formatDateTime(ts)} />
                      </Stack>
                      <Typography variant="body2" sx={styles.orderHeaderMeta}>{order.customerName || 'Client'} · {(order.items || []).length} article(s) · Net {currency(pricing.restaurantNet)} · Total {currency(pricing.total)}</Typography>
                      {order.cancellationReason ? <Typography variant="body2" sx={styles.orderHeaderError}>Raison: {order.cancellationReason}</Typography> : null}
                    </Box>
                    <IconButton size="small" onClick={() => setZoomOrder(order)} sx={styles.zoomIconButton} aria-label="Voir le détail de la commande">
                      <ZoomInIcon />
                    </IconButton>
                  </Box>
                  <OrderRecap order={order} compact />
                </Box>
              );
            }) : <EmptyState title="Aucun historique" text="Aucune commande ne correspond aux filtres choisis." />}
          </Box>
        )}

        {tab === 2 && (
          <Box sx={styles.panel}>
            <Box sx={styles.menuEditorHeader}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={styles.menuEditorTitle}>Gestion du menu</Typography>
                <Typography variant="body2" color="text.secondary">Ajoute les plats, remplace les photos et règle les options. Les modifications des plats se sauvegardent automatiquement.</Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" sx={GOLD_CHIP_SX} label={`${items.length} plat${items.length > 1 ? 's' : ''}`} />
                <Chip size="small" color="info" label="Sauvegarde auto" />
              </Stack>
            </Box>
            {menuFormState.status ? <Alert severity={menuFormState.status === 'error' ? 'error' : menuFormState.status === 'saved' ? 'success' : 'info'} sx={{ mb: 2 }}>{menuFormState.message}</Alert> : null}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={styles.formBox}>
                  <Typography sx={styles.sectionTitle}>Categorie</Typography>
                  <TextField fullWidth select size="small" label="Cadre" value={categoryDraft.presetId} onChange={event => setCategoryDraft({ ...categoryDraft, presetId: event.target.value })} SelectProps={{ native: true }}>
                    {MENU_CATEGORY_PRESETS.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
                  </TextField>
                  <TextField fullWidth size="small" label="Nom personnalise" value={categoryDraft.name} onChange={event => setCategoryDraft({ ...categoryDraft, name: event.target.value })} />
                  <Button variant="contained" onClick={saveCategory} disabled={menuFormState.status === 'saving'}>Ajouter</Button>
                </Paper>
              </Grid>
              <Grid item xs={12} md={8}>
                <Paper variant="outlined" sx={styles.formBox}>
                  <Typography sx={styles.sectionTitle}>Article</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Nom" value={itemDraft.name} onChange={event => setItemDraft({ ...itemDraft, name: event.target.value })} /></Grid>
                    <Grid item xs={6} md={2}><TextField fullWidth size="small" label="Prix restaurateur" value={itemDraft.price} onChange={event => setItemDraft({ ...itemDraft, price: event.target.value })} /></Grid>
                    <Grid item xs={6} md={3}>
                      <TextField fullWidth select size="small" label="Categorie" value={itemDraft.categoryId} onChange={event => setItemDraft({ ...itemDraft, categoryId: event.target.value })} SelectProps={{ native: true }}>
                        <option value="">Auto</option>
                        {MENU_CATEGORY_PRESETS.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}><Button fullWidth variant="contained" onClick={saveItem} disabled={menuFormState.status === 'saving'}>{menuFormState.status === 'saving' ? 'Sauvegarde...' : 'Ajouter'}</Button></Grid>
                    <Grid item xs={12}><TextField fullWidth size="small" label="Description" value={itemDraft.description} onChange={event => setItemDraft({ ...itemDraft, description: event.target.value })} /></Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        size="small"
                        label="Boissons proposées"
                        value={itemDraft.drinkOptionsText}
                        onChange={event => setItemDraft({ ...itemDraft, drinkOptionsText: event.target.value })}
                        helperText="Une boisson par ligne. Si renseigné, le client devra choisir sa boisson avant ajout au panier."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={5}
                        size="small"
                        label="Options du plat"
                        value={itemDraft.optionGroupsText}
                        onChange={event => setItemDraft({ ...itemDraft, optionGroupsText: event.target.value })}
                        helperText={'Format: Titre|min|max puis un choix par ligne. Séparez les groupes par une ligne vide. Exemple: Viandes|2|2 puis Poulet, Boeuf, Merguez.'}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Chip size="small" color="info" label={`Prix client estimé ${currency(itemPreviewPrice)} avec ${commissionPercent}% intégré`} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.5 }}>Allergenes</Typography>
                      <Box sx={styles.allergenGrid}>
                        {ALLERGEN_OPTIONS.map(allergen => {
                          const checked = normalizeAllergens(itemDraft.allergenIds).includes(allergen.id);
                          return (
                            <FormControlLabel
                              key={allergen.id}
                              control={<Checkbox size="small" checked={checked} onChange={() => {
                                const current = normalizeAllergens(itemDraft.allergenIds);
                                const next = checked ? current.filter(id => id !== allergen.id) : [...current, allergen.id];
                                setItemDraft({ ...itemDraft, allergenIds: next });
                              }} />}
                              label={allergen.label}
                              sx={styles.allergenCheck}
                            />
                          );
                        })}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <EatMediaInput
                        label="Photo du plat"
                        value={itemDraft.imageUrl}
                        storage={storage}
                        pathPrefix={`eat/menus/${restaurantId || 'draft'}/items`}
                        onChange={imageUrl => setItemDraft({ ...itemDraft, imageUrl })}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>

            <Box sx={styles.sectionSummaryGrid}>
              {topProducts.length ? (
                <Box sx={{ ...styles.sectionSummaryCard, borderColor: '#b8941f', backgroundColor: '#faf3d9' }}>
                  <Box sx={{ ...styles.sectionSummaryDot, backgroundColor: '#d4af37' }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ ...styles.sectionSummaryTitle, color: '#8a6d16' }}>Top 3 commandes</Typography>
                    <Typography sx={styles.sectionSummaryCount}>{topProducts.map(item => `${item.rank}. ${item.name}`).join(' - ')}</Typography>
                  </Box>
                </Box>
              ) : null}
              {menuSections.map(section => (
                <Box key={section.id} sx={{ ...styles.sectionSummaryCard, borderColor: section.color, backgroundColor: section.backgroundColor }}>
                  <Box sx={{ ...styles.sectionSummaryDot, backgroundColor: section.color }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ ...styles.sectionSummaryTitle, color: section.color }}>{section.name}</Typography>
                    <Typography sx={styles.sectionSummaryCount}>{section.items.length} article{section.items.length > 1 ? 's' : ''}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {items.length ? menuSections.filter(section => section.items.length).map(section => (
              <Box key={section.id} sx={{ ...styles.menuSectionBlock, borderColor: section.color, backgroundColor: section.backgroundColor }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={styles.menuSectionHeader}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box sx={{ ...styles.menuSectionRail, backgroundColor: section.color }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ ...styles.menuSectionTitle, color: section.color }}>{section.name}</Typography>
                      <Typography variant="body2" color="text.secondary">Section publique du menu</Typography>
                    </Box>
                  </Stack>
                  <Chip size="small" label={`${section.items.length} article${section.items.length > 1 ? 's' : ''}`} sx={{ backgroundColor: section.color, color: '#fff', fontWeight: 800 }} />
                </Stack>
                {section.items.map(item => {
                  const itemSave = menuSaveState[item.id] || {};
                  const allergens = normalizeAllergens(item.allergenIds || item.allergens);
                  const topRank = topProductRankById[item.id] || item.topProductRank || item.topRank;
                  return (
                  <Box key={item.id} sx={styles.menuItemBlock}>
                    <Box sx={styles.menuRow}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box sx={styles.dishImageBox}>{item.imageUrl ? <img src={item.imageUrl} alt="" style={styles.dishImage} /> : <LocalDiningIcon sx={{ color: '#8a94a6' }} />}</Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={styles.rowTitle}>{item.name}</Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>{item.description || categoryLabel(item.categoryId)} - {section.name} - Prix resto {currency(getRestaurantItemPrice(item, commissionRate))} - Prix client {currency(getClientItemPrice(item, commissionRate))}</Typography>
                          {item.drinkOptions && item.drinkOptions.length ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Boissons: {item.drinkOptions.join(', ')}</Typography> : null}
                          {item.optionGroups && item.optionGroups.length ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Options: {item.optionGroups.map(group => `${group.title || group.name} (${group.min || 0}-${group.max || group.min || 1})`).join(', ')}</Typography> : null}
                          {allergens.length ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Allergenes: {allergens.map(allergenLabel).join(', ')}</Typography> : null}
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {itemSave.status ? <Chip size="small" color={itemSave.status === 'error' ? 'error' : itemSave.status === 'saved' ? 'success' : 'info'} label={itemSave.message} /> : null}
                        {topRank ? <Chip size="small" sx={GOLD_CHIP_SX} label={`Top ${topRank}`} /> : null}
                        {item.imageUrl && <Chip size="small" label="Photo" />}
                        <Chip size="small" color={item.active ? undefined : 'default'} sx={item.active ? GOLD_CHIP_SX : undefined} label={item.active ? 'Disponible' : 'Masque'} />
                        <Switch checked={Boolean(item.active)} onChange={() => toggleItem(item)} />
                      </Stack>
                    </Box>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="Section"
                      value={menuSectionId(item, categoryLookup)}
                      onChange={event => updateItem(item, { categoryId: event.target.value })}
                      SelectProps={{ native: true }}
                      sx={{ mt: 1 }}
                    >
                      {MENU_CATEGORY_PRESETS.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
                    </TextField>
                    <Grid container spacing={1.25} sx={{ mt: 0.25 }}>
                      <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" label="Nom du plat" defaultValue={item.name || ''} onBlur={event => event.target.value !== (item.name || '') && updateItem(item, { name: event.target.value })} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField fullWidth size="small" label="Prix restaurateur" defaultValue={getRestaurantItemPrice(item, commissionRate)} onBlur={event => { const next = sanitizePrice(event.target.value); if (next !== getRestaurantItemPrice(item, commissionRate)) updateItem(item, { price: next, restaurantPrice: next, basePrice: next }); }} />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <TextField fullWidth size="small" label="Description" defaultValue={item.description || ''} onBlur={event => event.target.value !== (item.description || '') && updateItem(item, { description: event.target.value })} />
                      </Grid>
                    </Grid>
                    <EatMediaInput
                      label={`Modifier photo - ${item.name}`}
                      value={item.imageUrl || ''}
                      storage={storage}
                      pathPrefix={`eat/menus/${restaurantId || 'draft'}/items/${item.id}`}
                      onChange={async imageUrl => {
                        const saved = await updateItem(item, { imageUrl });
                        if (!saved) throw new Error('La photo n’a pas pu être sauvegardée.');
                      }}
                    />
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      label={`Boissons - ${item.name}`}
                      defaultValue={formatOptionLines(item.drinkOptions)}
                      onBlur={event => updateItem(item, { drinkOptions: parseOptionLines(event.target.value) })}
                      helperText="Une boisson par ligne. Sauvegarde à la sortie du champ."
                      sx={{ mt: 1 }}
                    />
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      size="small"
                      label={`Options du plat - ${item.name}`}
                      defaultValue={formatOptionGroupsText(item.optionGroups)}
                      onBlur={event => updateItem(item, { optionGroups: parseOptionGroupsText(event.target.value) })}
                      helperText={'Sauvegarde à la sortie du champ. Exemple: Viandes|2|2 puis Poulet, Boeuf, Merguez. Une ligne vide entre deux groupes.'}
                      sx={{ mt: 1 }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 800, mt: 1 }}>Allergenes du plat</Typography>
                    <Box sx={styles.allergenGrid}>
                      {ALLERGEN_OPTIONS.map(allergen => {
                        const checked = allergens.includes(allergen.id);
                        return (
                          <FormControlLabel
                            key={allergen.id}
                            control={<Checkbox size="small" checked={checked} onChange={() => {
                              const next = checked ? allergens.filter(id => id !== allergen.id) : [...allergens, allergen.id];
                              updateItem(item, { allergens: next, allergenIds: next });
                            }} />}
                            label={allergen.label}
                            sx={styles.allergenCheck}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                );
                })}
              </Box>
            )) : <EmptyState title="Menu vide" text="Ajoute les premiers articles pour ouvrir le restaurant." />}
          </Box>
        )}

        {tab === 3 && (
          <Box sx={styles.panel}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={styles.settingsBox}>
                  <Typography sx={styles.sectionTitle}>Categorie publique</Typography>
                  <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.5 }}>Adresse réelle</Typography>
                      <Box sx={{ border: '1px solid rgba(0,0,0,0.18)', borderRadius: 1.5, px: 1.25, py: 0.75 }}>
                        <GoogleMapsAutoComplete
                          variant="standard"
                          placeholder="Tapez l'adresse exacte (suggestions Google/OSM)"
                          value={restaurant ? { description: getRestaurantAddress(restaurant) } : null}
                          onChange={(selection) => applyAddressUpdate(selection)}
                        />
                      </Box>
                      {restaurant && restaurant.lat && restaurant.lng ? (
                        <Typography variant="caption" sx={{ color: '#8a6d16', display: 'block', mt: 0.5 }}>Position GPS confirmée ({restaurant.lat.toFixed(4)}, {restaurant.lng.toFixed(4)}) — vous apparaissez aux clients dans la zone.</Typography>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#b35900', display: 'block', mt: 0.5 }}>Sélectionnez une suggestion pour enregistrer la position GPS et apparaître sur l'app.</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth select size="small" label="Cuisine" value={(restaurant && restaurant.cuisine) || ''} onChange={event => updateRestaurant({ cuisine: event.target.value })} SelectProps={{ native: true }}>
                        <option value="">Choisir</option>
                        {EAT_CUISINES.map(cuisine => <option key={cuisine.id} value={cuisine.id}>{cuisine.emoji} {cuisine.label}</option>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth size="small" label="Specialite" value={(restaurant && restaurant.specialty) || ''} onChange={event => updateRestaurant({ specialty: event.target.value })} />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={styles.settingsBox}>
                  <Typography sx={styles.sectionTitle}>Branding</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {BRAND_ACCENTS.map(brand => <Button key={brand.id} size="small" variant={restaurant && restaurant.brandColor === brand.color ? 'contained' : 'outlined'} onClick={() => updateRestaurant({ brandColor: brand.color })} sx={{ borderColor: brand.color, color: restaurant && restaurant.brandColor === brand.color ? '#fff' : brand.color, backgroundColor: restaurant && restaurant.brandColor === brand.color ? brand.color : 'transparent' }}>{brand.label}</Button>)}
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={styles.settingsBox}>
                  <Typography sx={styles.sectionTitle}>Logo</Typography>
                  <EatMediaInput
                    label="Logo"
                    value={(restaurant && restaurant.logoUrl) || ''}
                    storage={storage}
                    pathPrefix={`eat/restaurants/${restaurantId || 'draft'}/logo`}
                    onChange={logoUrl => updateRestaurant({ logoUrl })}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={styles.settingsBox}>
                  <Typography sx={styles.sectionTitle}>Photo vitrine</Typography>
                  <EatMediaInput
                    label="Photo couverture"
                    value={(restaurant && restaurant.coverImageUrl) || ''}
                    storage={storage}
                    pathPrefix={`eat/restaurants/${restaurantId || 'draft'}/cover`}
                    onChange={coverImageUrl => updateRestaurant({ coverImageUrl })}
                  />
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    <Button size="small" variant={!coverImageUrl ? 'contained' : 'outlined'} onClick={() => updateRestaurant({ coverImageUrl: '' })}>Motif Allô</Button>
                    {DEFAULT_EAT_HEADERS.map(header => (
                      <Button key={header.id} size="small" variant={coverImageUrl === header.imageUrl ? 'contained' : 'outlined'} onClick={() => updateRestaurant({ coverImageUrl: header.imageUrl })}>{header.label}</Button>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={styles.settingsBox}>
                  <Typography sx={styles.sectionTitle}>K-bis obligatoire</Typography>
                  <EatMediaInput
                    label="K-bis"
                    value={(restaurant && (restaurant.kbisUrl || restaurant.kbis)) || ''}
                    storage={storage}
                    pathPrefix={`eat/restaurants/${restaurantId || 'draft'}/kbis`}
                    documentMode
                    onChange={kbisUrl => updateRestaurant({ kbisUrl })}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={styles.settingsBox}>
                  <Typography sx={styles.sectionTitle}>Coordonnées bancaires (RIB)</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>Saisissez votre IBAN/BIC (méthode recommandée). Le téléversement de la photo du RIB reste optionnel.</Typography>
                  <Grid container spacing={1.25}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        size="small"
                        label="IBAN"
                        placeholder="FR76 0000 0000 0000 0000 0000 000"
                        defaultValue={getRestaurantIban(restaurant || '')}
                        onBlur={event => {
                          const value = String(event.target.value || '').replace(/\s+/g, '').toUpperCase();
                          if (value !== getRestaurantIban(restaurant || '')) updateRestaurant({ iban: value });
                        }}
                        inputProps={{ style: { textTransform: 'uppercase', letterSpacing: 1 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="BIC"
                        placeholder="BNPAFRPPXXX"
                        defaultValue={getRestaurantBic(restaurant || '')}
                        onBlur={event => {
                          const value = String(event.target.value || '').replace(/\s+/g, '').toUpperCase();
                          if (value !== getRestaurantBic(restaurant || '')) updateRestaurant({ bic: value });
                        }}
                        inputProps={{ style: { textTransform: 'uppercase', letterSpacing: 1 } }}
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, mb: 0.25 }}>Photo du RIB (optionnel)</Typography>
                  <EatMediaInput
                    label="RIB (photo ou PDF)"
                    value={(restaurant && (restaurant.ribUrl || restaurant.rib)) || ''}
                    storage={storage}
                    pathPrefix={`eat/restaurants/${restaurantId || 'draft'}/rib`}
                    documentMode
                    onChange={ribUrl => updateRestaurant({ ribUrl })}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={styles.settingsBox}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography sx={styles.sectionTitle}>Ouverture</Typography>
                      <Typography variant="body2" color="text.secondary">{restaurant && restaurant.isOpen ? 'La boutique accepte les commandes.' : 'La boutique ne prend pas de commande.'}</Typography>
                    </Box>
                    <Switch checked={Boolean(restaurant && restaurant.isOpen)} onChange={() => updateRestaurant({ isOpen: !(restaurant && restaurant.isOpen) })} />
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={styles.settingsBox}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography sx={styles.sectionTitle}>Publication</Typography>
                      <Typography variant="body2" color="text.secondary">{restaurantDocsComplete ? (restaurant && restaurant.active !== false ? 'Restaurant visible dans Allô Driver Eats.' : 'Restaurant masqué dans Allô Driver Eats.') : 'K-bis et RIB (IBAN ou photo) obligatoires avant publication.'}</Typography>
                    </Box>
                    <Switch checked={Boolean(restaurant && restaurant.active !== false && restaurantDocsComplete)} onChange={() => updateRestaurant({ active: restaurant && restaurant.active === false ? restaurantDocsComplete : false })} />
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={styles.settingsBox}>
                  <Typography sx={styles.sectionTitle}>Horaires disponibles</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{restaurant ? getOpeningSummary(restaurant) : 'Horaires non renseignés'}</Typography>
                  <Grid container spacing={1.25}>
                    {DAY_CONFIG.map(day => {
                      const dayValue = getOpeningHours(restaurant || {})[day.key] || {};
                      return (
                        <Grid item xs={12} md={6} lg={4} key={day.key}>
                          <Paper variant="outlined" sx={styles.dayScheduleCard}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={styles.rowTitle}>{day.label}</Typography>
                                <Typography variant="body2" color="text.secondary">{formatDaySchedule(dayValue)}</Typography>
                              </Box>
                              <Switch checked={dayValue.open !== false} onChange={() => updateOpeningDay(day.key, { open: dayValue.open === false })} />
                            </Stack>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              <TextField fullWidth size="small" type="time" label="Début" value={dayValue.start || '11:30'} onChange={event => updateOpeningDay(day.key, { start: event.target.value })} InputLabelProps={{ shrink: true }} disabled={dayValue.open === false} />
                              <TextField fullWidth size="small" type="time" label="Fin" value={dayValue.end || '22:30'} onChange={event => updateOpeningDay(day.key, { end: event.target.value })} InputLabelProps={{ shrink: true }} disabled={dayValue.open === false} />
                            </Stack>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">Horaires en coupure</Typography>
                              <Switch
                                size="small"
                                checked={Boolean(dayValue.split)}
                                disabled={dayValue.open === false}
                                onChange={() => updateOpeningDay(day.key, dayValue.split
                                  ? { split: false }
                                  : { split: true, start2: dayValue.start2 || '19:00', end2: dayValue.end2 || '22:30' })}
                              />
                            </Stack>
                            {dayValue.split && dayValue.open !== false ? (
                              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <TextField fullWidth size="small" type="time" label="Reprise" value={dayValue.start2 || '19:00'} onChange={event => updateOpeningDay(day.key, { start2: event.target.value })} InputLabelProps={{ shrink: true }} />
                                <TextField fullWidth size="small" type="time" label="Fermeture" value={dayValue.end2 || '22:30'} onChange={event => updateOpeningDay(day.key, { end2: event.target.value })} InputLabelProps={{ shrink: true }} />
                              </Stack>
                            ) : null}
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
      <Dialog open={Boolean(cancelOrder)} onClose={() => setCancelOrder(null)} fullWidth maxWidth="xs">
        <DialogTitle>Annuler la commande</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{cancelOrder ? cancelOrder.reference || cancelOrder.id : ''}</Typography>
          <TextField fullWidth select size="small" label="Raison" value={cancelReason} onChange={event => setCancelReason(event.target.value)} SelectProps={{ native: true }}>
            {cancelReasons.map(reason => <option key={reason} value={reason}>{reason}</option>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOrder(null)}>Retour</Button>
          <Button color="error" variant="contained" onClick={confirmCancelOrder}>Confirmer l'annulation</Button>
        </DialogActions>
      </Dialog>

      <OrderZoomDialog
        order={zoomOrder}
        onClose={() => setZoomOrder(null)}
        onAccept={order => { updateOrder(order, 'RESTAURANT_ACCEPTED'); setZoomOrder(null); }}
        onPrepare={order => updateOrder(order, 'PREPARING')}
        onReady={order => { updateOrder(order, 'READY_FOR_PICKUP'); setZoomOrder(null); }}
        onCancel={order => { setZoomOrder(null); openCancelDialog(order); }}
      />

      <EatChatDialog order={chatOrder} onClose={() => setChatOrder(null)} dispatch={dispatch} />

      <Dialog open={iosInstallOpen} onClose={() => setIosInstallOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TabletMacIcon fontSize="small" /> Installer sur votre tablette
        </DialogTitle>
        <DialogContent>
          {pwaPlatform.os === 'ios' ? (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>Sur iPhone / iPad (Safari), voici comment installer l'application en 3 étapes :</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>① Appuyez sur le bouton <strong>Partager</strong> <span style={{ fontFamily: 'monospace' }}>⎙</span> en bas de Safari</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>② Faites défiler et appuyez sur <strong>« Sur l'écran d'accueil »</strong></Typography>
              <Typography variant="body2">③ Appuyez sur <strong>Ajouter</strong> en haut à droite</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>L'appli apparaîtra comme une vraie application sur votre écran d'accueil, sans barre de navigateur.</Typography>
            </>
          ) : pwaPlatform.os === 'mac' && !pwaPlatform.isFirefox ? (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>Sur Mac (Safari), voici comment installer l'application en 3 étapes :</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>① Cliquez sur le bouton <strong>Partager</strong> <span style={{ fontFamily: 'monospace' }}>⎙</span> dans la barre de Safari</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>② Sélectionnez <strong>« Ajouter au Dock »</strong> (Safari 17+/macOS Sonoma) ou <strong>« Ouvrir dans une fenêtre séparée »</strong></Typography>
              <Typography variant="body2">③ Confirmez avec <strong>Ajouter</strong></Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Sur une version plus ancienne de Safari, utilisez plutôt Google Chrome ou Microsoft Edge sur ce Mac pour installer l'application.</Typography>
            </>
          ) : pwaPlatform.isFirefox ? (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>Firefox ne permet pas d'installer l'application comme une appli native.</Typography>
              <Typography variant="body2" color="text.secondary">Ouvrez ce site avec <strong>Google Chrome</strong>, <strong>Microsoft Edge</strong> ou <strong>Safari</strong> (sur iPhone/iPad/Mac) pour pouvoir l'installer sur cet écran.</Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>Pour installer l'application sur cet écran (Android, Windows...) :</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>① Ouvrez ce site dans <strong>Google Chrome</strong> ou <strong>Microsoft Edge</strong></Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>② Appuyez/cliquez sur l'icône <strong>⊕</strong> ou les 3 points en haut à droite du navigateur</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>③ Sélectionnez <strong>« Installer l'application »</strong> ou <strong>« Ajouter à l'écran d'accueil »</strong></Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Une fois installée, l'appli fonctionne comme une vraie application — même hors connexion partielle — et reste toujours accessible depuis votre bureau ou écran d'accueil.</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIosInstallOpen(false)}>Compris</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(liveToast)} autoHideDuration={6000} onClose={() => setLiveToast('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setLiveToast('')}>{liveToast}</Alert>
      </Snackbar>
      <Snackbar open={Boolean(errorToast)} autoHideDuration={8000} onClose={() => setErrorToast('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" variant="filled" onClose={() => setErrorToast('')}>{errorToast}</Alert>
      </Snackbar>
    </Box>
  );
}

function OrderRecap({ order, compact = false }) {
  const pricing = getOrderPricing(order);
  const items = order.items || [];
  return (
    <Box sx={compact ? styles.orderRecapCompact : styles.orderRecap}>
      {items.map(item => (
        <Box key={item.id || item.name} sx={styles.recapLine}>
          <Typography variant="body2" color="text.secondary">{Number(item.qty || 1)}x {item.name || 'Article'}{item.selectedDrink ? ` - Boisson: ${item.selectedDrink}` : ''}</Typography>
          <Typography variant="body2" sx={styles.recapValue}>{currency(Number(item.restaurantPrice || item.basePrice || item.originalPrice || item.price || 0) * Number(item.qty || 1))}</Typography>
        </Box>
      ))}
      <Box sx={styles.recapLine}><Typography variant="body2" color="text.secondary">Sous-total restaurant</Typography><Typography variant="body2" sx={styles.recapValue}>{currency(pricing.restaurantSubtotal)}</Typography></Box>
      {pricing.platformNet > 0 ? <Box sx={styles.recapLine}><Typography variant="body2" color="text.secondary">Commission intégrée client</Typography><Typography variant="body2" sx={styles.recapValue}>{currency(pricing.platformNet)}</Typography></Box> : null}
      <Box sx={styles.recapLine}><Typography variant="body2" color="text.secondary">Livraison client</Typography><Typography variant="body2" sx={styles.recapValue}>{currency(pricing.deliveryFee)}</Typography></Box>
      <Box sx={styles.recapLine}><Typography variant="body2" color="text.secondary">Total client</Typography><Typography variant="body2" sx={styles.recapValue}>{currency(pricing.total)}</Typography></Box>
      <Box sx={styles.recapTotal}><Typography variant="body2" sx={styles.recapValue}>Montant restaurant</Typography><Typography variant="body2" sx={styles.recapValue}>{currency(pricing.restaurantNet)}</Typography></Box>
    </Box>
  );
}

// Vue "bon de commande" agrandie — les restaurateurs se plaignaient que les
// commandes n'étaient pas assez visibles/lisibles en cuisine. Reprend les
// mêmes données que OrderRecap mais en très gros, plein écran sur mobile/tablette.
function OrderZoomDialog({ order, onClose, onAccept, onPrepare, onReady, onCancel }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  if (!order) return null;
  const pricing = getOrderPricing(order);
  const items = order.items || [];
  const isNew = isNewRestaurantOrder(order);
  const address = (order.deliveryAddress && (order.deliveryAddress.address || order.deliveryAddress.add)) || order.dropAddress || '';
  return (
    <Dialog open={Boolean(order)} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="sm" sx={styles.zoomDialog}>
      <Box sx={styles.zoomHeader}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={styles.zoomHeaderTitle}>{order.reference || order.id}</Typography>
            <Chip size="small" color={isNew ? 'error' : undefined} sx={isNew ? undefined : GOLD_CHIP_SX} label={statusLabels[order.status] || order.status || 'Nouvelle'} />
          </Stack>
          <Typography sx={styles.zoomHeaderMeta}>{formatDateTime(getOrderTimestamp(order))}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="Fermer"><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={styles.zoomContent}>
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center"><PersonIcon sx={{ color: '#8a6d16' }} /><Typography sx={styles.zoomCustomer}>{order.customerName || order.customerId || 'Client'}</Typography></Stack>
          {order.customerPhone ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <PhoneIcon sx={{ color: '#8a6d16' }} />
              <Typography component="a" href={`tel:${order.customerPhone}`} sx={{ ...styles.zoomCustomer, color: '#172033', textDecoration: 'none' }}>{order.customerPhone}</Typography>
            </Stack>
          ) : null}
          {address ? (
            <Stack direction="row" spacing={1} alignItems="flex-start"><PlaceIcon sx={{ color: '#8a6d16', mt: 0.25 }} /><Typography sx={styles.zoomAddress}>{address}</Typography></Stack>
          ) : null}
        </Stack>

        {order.pickupCode && !['DELIVERED', 'CANCELLED'].includes(order.status) ? (
          <Box sx={styles.zoomPickupCode}>
            <Typography sx={{ fontWeight: 800, color: '#8a6d16' }}>Code de retrait livreur</Typography>
            <Typography sx={{ fontSize: { xs: 40, sm: 48 }, fontWeight: 900, letterSpacing: 6, color: '#172033', fontFamily: 'monospace', lineHeight: 1.2 }}>{order.pickupCode}</Typography>
          </Box>
        ) : null}

        <Divider sx={{ my: 2 }} />

        <Typography sx={styles.zoomSectionTitle}>{items.length} article(s) commandé(s)</Typography>
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {items.map((item, index) => {
            const optionLines = formatSelectedOptions(item);
            return (
              <Box key={item.id || `${item.name}-${index}`} sx={styles.zoomItemRow}>
                <Box sx={styles.zoomItemQty}>{Number(item.qty || 1)}×</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={styles.zoomItemName}>{item.name || 'Article'}</Typography>
                  {optionLines.length ? optionLines.map((line, lineIndex) => (
                    <Typography key={lineIndex} sx={styles.zoomItemOption}>• {line}</Typography>
                  )) : null}
                </Box>
                <Typography sx={styles.zoomItemPrice}>{currency(Number(item.restaurantPrice || item.basePrice || item.originalPrice || item.price || 0) * Number(item.qty || 1))}</Typography>
              </Box>
            );
          })}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={0.75}>
          <Box sx={styles.zoomPriceLine}><Typography sx={styles.zoomPriceLabel}>Sous-total restaurant</Typography><Typography sx={styles.zoomPriceValue}>{currency(pricing.restaurantSubtotal)}</Typography></Box>
          <Box sx={styles.zoomPriceLine}><Typography sx={styles.zoomPriceLabel}>Livraison client</Typography><Typography sx={styles.zoomPriceValue}>{currency(pricing.deliveryFee)}</Typography></Box>
          <Box sx={styles.zoomPriceLine}><Typography sx={styles.zoomPriceLabel}>Total client</Typography><Typography sx={styles.zoomPriceValue}>{currency(pricing.total)}</Typography></Box>
          <Box sx={styles.zoomPriceTotal}><Typography sx={styles.zoomPriceTotalLabel}>Montant restaurant</Typography><Typography sx={styles.zoomPriceTotalValue}>{currency(pricing.restaurantNet)}</Typography></Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={styles.zoomActions}>
        {['PLACED', 'PAID'].includes(order.status) && <Button fullWidth size="large" variant="contained" sx={{ bgcolor: '#d4af37', color: '#000', '&:hover': { bgcolor: '#b8941f' } }} startIcon={<RoomServiceIcon />} onClick={() => onAccept(order)}>Accepter</Button>}
        {['RESTAURANT_ACCEPTED'].includes(order.status) && <Button fullWidth size="large" variant="outlined" onClick={() => onPrepare(order)}>Préparation</Button>}
        {['RESTAURANT_ACCEPTED', 'PREPARING'].includes(order.status) && <Button fullWidth size="large" variant="contained" sx={{ bgcolor: '#111318' }} onClick={() => onReady(order)}>Prête retrait</Button>}
        {['PLACED', 'PAID', 'RESTAURANT_ACCEPTED', 'PREPARING'].includes(order.status) && <Button fullWidth size="large" color="error" variant="outlined" onClick={() => onCancel(order)}>Annuler</Button>}
      </DialogActions>
    </Dialog>
  );
}

// Chat restaurateur <-> livreur (aucun moyen de se joindre auparavant sur les
// commandes Eats). Thread separe de la conversation client<->livreur
// (order.id + '-restaurant') pour ne jamais melanger les deux fils. Reprend
// fetchChatMessages/stopFetchMessages/sendEatMessage deja utilises cote mobile.
function EatChatDialog({ order, onClose, dispatch }) {
  const chats = useSelector(state => state.chatdata.messages);
  const profile = useSelector(state => state.auth.profile || {});
  const [text, setText] = useState('');
  const threadId = order ? `${order.id}-restaurant` : null;

  useEffect(() => {
    if (!threadId) return undefined;
    dispatch(api.fetchChatMessages(threadId));
    return () => dispatch(api.stopFetchMessages(threadId));
  }, [threadId, dispatch]);

  useEffect(() => { setText(''); }, [threadId]);

  const sortedMessages = useMemo(() => {
    return [...(chats || [])].sort((left, right) => new Date(left.createdAt) - new Date(right.createdAt));
  }, [chats]);

  if (!order) return null;

  const send = () => {
    const message = text.trim();
    if (!message) return;
    dispatch(api.sendEatMessage({
      order: { status: order.status, courierId: order.courierId, restaurantId: order.restaurantId },
      role: 'restaurant',
      counterpartRole: 'courier',
      threadId,
      fromUid: profile.uid,
      message
    }));
    setText('');
  };

  return (
    <Dialog open={Boolean(order)} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Chat avec {order.courierName || 'le livreur'}
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ height: 380, overflowY: 'auto', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
        {sortedMessages.length ? sortedMessages.map((msg, index) => (
          <Box key={index} sx={{ display: 'flex', justifyContent: msg.source === 'restaurant' ? 'flex-end' : 'flex-start', mb: 1 }}>
            <Box sx={{
              maxWidth: '75%', px: 1.5, py: 1, borderRadius: 2,
              bgcolor: msg.source === 'restaurant' ? '#111318' : '#fff',
              color: msg.source === 'restaurant' ? '#fff' : '#111318',
              border: msg.source === 'restaurant' ? 'none' : '1px solid #e2e8f0'
            }}>
              <Typography variant="body2">{msg.message}</Typography>
            </Box>
          </Box>
        )) : <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>Aucun message pour le moment.</Typography>}
      </DialogContent>
      <DialogActions sx={{ p: 1.5, gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Votre message..."
          value={text}
          onChange={event => setText(event.target.value)}
          onKeyDown={event => { if (event.key === 'Enter') send(); }}
        />
        <Button variant="contained" sx={{ bgcolor: '#d4af37', color: '#000', '&:hover': { bgcolor: '#b8941f' } }} endIcon={<SendIcon />} onClick={send} disabled={!text.trim()}>Envoyer</Button>
      </DialogActions>
    </Dialog>
  );
}

function Metric({ icon, label, value }) {
  return (
    <Paper sx={styles.metricCard}>
      <Box sx={styles.metricIcon}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={styles.metricValue} noWrap>{value}</Typography>
        <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
      </Box>
    </Paper>
  );
}

function EmptyState({ title, text }) {
  return (
    <Box sx={styles.emptyState}>
      <StorefrontIcon sx={{ color: '#90a4ae', fontSize: 38 }} />
      <Typography sx={styles.sectionTitle}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{text}</Typography>
    </Box>
  );
}

function EatHeaderMotif({ restaurant }) {
  const safe = restaurant || {};
  return (
    <Box sx={styles.headerMotif}>
      <Box sx={styles.headerMotifBand} />
      <Box sx={styles.headerMotifMark}>Allô Driver Eats</Box>
      <Box sx={styles.headerMotifMeta}>{safe.specialty || cuisineLabel(safe.cuisine)}</Box>
    </Box>
  );
}

function EatMediaInput({ label, value, storage, pathPrefix, onChange, documentMode = false }) {
  const [draftUrl, setDraftUrl] = useState(value || '');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const inputId = useMemo(() => `eat-media-${makeId('input', label)}`, [label]);

  useEffect(() => {
    setDraftUrl(value || '');
  }, [value]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    setProgress(0);
    try {
      const nextUrl = await uploadEatMedia(storage, pathPrefix, file, setProgress, documentMode);
      setDraftUrl(nextUrl);
      await onChange(nextUrl);
    } catch (uploadError) {
      setError(uploadError && uploadError.message ? uploadError.message : 'Upload impossible. Vérifiez le fichier et réessayez.');
    } finally {
      setUploading(false);
      setDragging(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files && event.dataTransfer.files[0];
    handleFile(file);
  };

  const applyUrl = () => {
    if (!draftUrl.trim()) return;
    setError('');
    Promise.resolve(onChange(draftUrl.trim())).catch(saveError => {
      setError(saveError && saveError.message ? saveError.message : 'Sauvegarde impossible. Réessayez.');
    });
  };

  const clearMedia = () => {
    setDraftUrl('');
    setError('');
    setProgress(0);
    Promise.resolve(onChange('')).catch(saveError => {
      setError(saveError && saveError.message ? saveError.message : 'Sauvegarde impossible. Réessayez.');
    });
  };

  const isImageDocument = /\.(png|jpe?g|webp|gif)(\?|#|$)/i.test(draftUrl || '');

  return (
    <Paper
      variant="outlined"
      sx={{ ...styles.mediaDrop, borderColor: dragging ? '#d4af37' : '#d9e0ea', backgroundColor: dragging ? '#faf3d9' : '#fff' }}
      onDragOver={event => { event.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box sx={styles.mediaPreview}>
          {draftUrl && (!documentMode || isImageDocument) ? <img src={draftUrl} alt="" style={styles.mediaPreviewImage} /> : draftUrl ? <ReceiptLongIcon sx={{ color: '#8a94a6' }} /> : <CloudUploadIcon sx={{ color: '#8a94a6' }} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={styles.sectionTitle}>{label}</Typography>
              <Typography variant="body2" color="text.secondary">{documentMode ? 'Déposez une image ou un PDF. L’URL reste disponible en dépannage.' : 'Déposez une image ou importez un fichier local. L’URL reste disponible en dépannage.'}</Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="contained" component="label" startIcon={<AddPhotoAlternateIcon />} disabled={uploading || !storage}>
                {uploading ? 'Import...' : 'Importer'}
                <input id={inputId} hidden accept={documentMode ? 'image/png,image/jpeg,image/webp,application/pdf' : 'image/png,image/jpeg,image/webp'} type="file" onChange={event => handleFile(event.target.files && event.target.files[0])} />
              </Button>
              <Button variant="outlined" color="inherit" startIcon={<DeleteOutlineIcon />} onClick={clearMedia} disabled={uploading || !draftUrl}>Retirer</Button>
            </Stack>
          </Stack>
          {uploading ? <LinearProgress variant="determinate" value={progress} sx={{ mt: 1.25, height: 7, borderRadius: 999 }} /> : null}
          {error ? <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert> : null}
          {!storage ? <Alert severity="warning" sx={{ mt: 1 }}>Firebase Storage n’est pas initialisé. L’upload fichier est indisponible sur cette session.</Alert> : null}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 1.25 }}>
            <TextField fullWidth size="small" label={documentMode ? 'URL document optionnelle' : 'URL image optionnelle'} value={draftUrl} onChange={event => setDraftUrl(event.target.value)} />
            <Button variant="outlined" startIcon={<LinkIcon />} onClick={applyUrl} disabled={!draftUrl || uploading}>Appliquer</Button>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

const styles = {
  page: {
    p: { xs: 1.25, sm: 1.75, md: 2.25 },
    backgroundColor: '#f6f8fb',
    minHeight: 'calc(100vh - 64px)'
  },
  header: {
    mb: 2,
    alignItems: { xs: 'stretch', md: 'center' }
  },
  title: {
    fontWeight: 900,
    color: '#172033',
    fontSize: { xs: 24, md: 32 },
    lineHeight: 1.15
  },
  metricCard: {
    p: { xs: 1.5, md: 1.9 },
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    minHeight: { xs: 88, md: 96 },
    borderRadius: 2,
    border: '1px solid rgba(226, 232, 240, 0.95)',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)'
  },
  metricIcon: {
    width: 42,
    height: 42,
    borderRadius: '10px',
    display: 'grid',
    placeItems: 'center',
    backgroundColor: '#111318',
    color: '#d4af37',
    flex: '0 0 auto'
  },
  metricValue: {
    fontWeight: 900,
    fontSize: 22,
    lineHeight: 1.1,
    color: '#172033'
  },
  notificationPanel: {
    p: 1.75,
    mb: 2,
    borderRadius: 2,
    border: '1px solid #f3d76b',
    backgroundColor: '#fffaf0',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)'
  },
  liveOpsPanel: {
    p: { xs: 1.5, md: 2 },
    mb: 2,
    borderRadius: 2,
    border: '1px solid #e1d8b8',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
    color: '#172033',
    '& .MuiChip-root': {
      color: '#172033',
      fontWeight: 800,
      backgroundColor: '#eef2f7'
    },
    '& .MuiChip-colorSuccess': {
      backgroundColor: '#d4af37',
      color: '#000'
    },
    '& .MuiChip-colorWarning': {
      backgroundColor: '#ed6c02',
      color: '#111318'
    },
    '& .MuiChip-colorError': {
      backgroundColor: '#d32f2f',
      color: '#fff'
    },
    '& .MuiChip-icon': {
      color: 'inherit'
    },
    '& .MuiButton-outlined': {
      color: '#8a6d16',
      borderColor: '#d4af37'
    },
    '& .MuiButton-outlined:hover': {
      borderColor: '#b8941f',
      backgroundColor: '#faf3d9'
    },
    '& .MuiButton-outlined.Mui-disabled': {
      color: '#94a3b8',
      borderColor: '#e2e8f0'
    }
  },
  liveOpsIcon: {
    width: 52,
    height: 52,
    borderRadius: 2,
    display: 'grid',
    placeItems: 'center',
    backgroundColor: '#111318',
    color: '#d4af37',
    flex: '0 0 auto'
  },
  liveOpsTitle: {
    fontWeight: 900,
    fontSize: { xs: 18, md: 22 },
    color: '#172033'
  },
  liveOpsText: {
    color: '#667085'
  },
  notificationIcon: {
    width: 42,
    height: 42,
    borderRadius: '10px',
    display: 'grid',
    placeItems: 'center',
    backgroundColor: '#f2cc38',
    color: '#172033',
    flex: '0 0 auto'
  },
  workspace: {
    borderRadius: 2,
    overflow: 'hidden',
    border: '1px solid #dfe5ee',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
    backgroundColor: '#ffffff'
  },
  tabs: {
    borderBottom: '1px solid #dfe5ee',
    px: 1,
    backgroundColor: '#ffffff',
    '& .MuiTab-root': {
      color: '#52606f',
      fontWeight: 800,
      textTransform: 'none',
      minHeight: 52
    },
    '& .MuiTab-root.Mui-selected': {
      color: '#111318'
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#d4af37',
      height: 3,
      borderRadius: 2
    }
  },
  panel: {
    p: { xs: 1.25, sm: 1.75, md: 2 },
    backgroundColor: '#fff'
  },
  orderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    py: 1.5,
    borderBottom: '1px solid #edf0f3',
    flexDirection: { xs: 'column', md: 'row' }
  },
  orderCard: {
    mb: 1.5,
    border: '1px solid #edf0f3',
    borderRadius: 2,
    backgroundColor: '#fff',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
    overflow: 'hidden'
  },
  newOrderCard: {
    mb: 1.5,
    border: '2px solid #ef4444',
    borderRadius: 2,
    backgroundColor: '#fff',
    boxShadow: '0 8px 24px rgba(239, 68, 68, 0.16)',
    overflow: 'hidden'
  },
  orderHeaderBar: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    p: { xs: 1.5, md: 1.75 },
    backgroundColor: '#111318',
    // Reste empilé (titre au-dessus des boutons) jusqu'à lg: en ligne dès md,
    // le titre + 3 boutons d'action se disputaient l'espace sur tablette
    // (portrait comme paysage) et finissaient collés/écrasés les uns sur les autres.
    flexDirection: { xs: 'column', lg: 'row' }
  },
  kitchenActionsWrap: {
    width: { xs: '100%', lg: 'auto' }
  },
  kitchenActions: {
    width: { xs: '100%', lg: 'auto' },
    '& .MuiButton-root': {
      minHeight: 48,
      borderRadius: 2,
      fontWeight: 900,
      // Grille 2 colonnes sur tablette (place confortable pour chaque bouton),
      // pleine largeur sur téléphone, taille naturelle en ligne sur desktop.
      flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', lg: '1 1 auto' }
    }
  },
  kitchenActionsSecondary: {
    width: '100%',
    '& .MuiButton-root': {
      minHeight: 46,
      borderRadius: 2,
      fontWeight: 900,
      flex: 1
    }
  },
  headerOutlinedButton: {
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.4)',
    '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' }
  },
  orderHeaderTitle: {
    fontWeight: 900,
    color: '#fff'
  },
  orderHeaderMeta: {
    color: '#c4c9d0'
  },
  orderHeaderError: {
    color: '#ffb4b4',
    mt: 0.5
  },
  orderRecap: {
    p: { xs: 1.5, md: 1.75 },
    display: 'grid',
    gap: 0.75
  },
  orderRecapCompact: {
    p: { xs: 1.5, md: 1.75 },
    display: 'grid',
    gap: 0.5
  },
  recapLine: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2
  },
  recapTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    pt: 1,
    mt: 0.5,
    borderTop: '1px solid #edf0f3'
  },
  recapValue: {
    fontWeight: 800,
    color: '#172033',
    whiteSpace: 'nowrap'
  },
  menuRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    py: 1.5,
    alignItems: 'center',
    flexDirection: { xs: 'column', md: 'row' }
  },
  menuEditorHeader: {
    mb: 2,
    p: 2,
    borderRadius: 2,
    border: '1px solid #dfe5ee',
    backgroundColor: '#f8fafc',
    display: 'flex',
    alignItems: { xs: 'flex-start', md: 'center' },
    justifyContent: 'space-between',
    gap: 2,
    flexDirection: { xs: 'column', md: 'row' }
  },
  menuEditorTitle: {
    fontWeight: 900,
    color: '#172033',
    fontSize: 22,
    lineHeight: 1.15
  },
  menuItemBlock: {
    p: 1.75,
    mb: 1.5,
    border: '1px solid #e2eadf',
    borderRadius: 2,
    backgroundColor: '#fff',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)'
  },
  sectionSummaryGrid: {
    mb: 2,
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
    gap: 1
  },
  sectionSummaryCard: {
    minHeight: 74,
    borderRadius: 2,
    border: '1px solid',
    p: 1.25,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.04)'
  },
  sectionSummaryDot: {
    width: 10,
    height: 42,
    borderRadius: 5,
    flex: '0 0 auto'
  },
  sectionSummaryTitle: {
    fontWeight: 900,
    fontSize: 14,
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  sectionSummaryCount: {
    color: '#64748b',
    fontSize: 12,
    fontWeight: 800,
    mt: 0.35
  },
  menuSectionBlock: {
    mb: 2.25,
    borderRadius: 2,
    border: '1px solid',
    p: { xs: 1, md: 1.25 }
  },
  menuSectionHeader: {
    px: 1,
    py: 1,
    mb: 0.75
  },
  menuSectionRail: {
    width: 8,
    height: 44,
    borderRadius: 4,
    flex: '0 0 auto'
  },
  menuSectionTitle: {
    fontWeight: 900,
    fontSize: 18
  },
  rowTitle: {
    fontWeight: 800,
    color: '#172033'
  },
  sectionTitle: {
    fontWeight: 800,
    color: '#172033'
  },
  formBox: {
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    minHeight: '100%',
    borderRadius: 2,
    borderColor: '#e1e8ef',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.04)'
  },
  allergenGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, minmax(0, 1fr))' },
    gap: 0.5,
    p: 1,
    border: '1px solid #e1e8ef',
    borderRadius: 2,
    backgroundColor: '#fff'
  },
  allergenCheck: {
    m: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: 13,
      fontWeight: 700,
      color: '#334155'
    }
  },
  dayScheduleCard: {
    p: 1.25,
    borderRadius: 2,
    backgroundColor: '#fff'
  },
  brandBanner: {
    position: 'relative',
    mb: 2,
    height: { xs: 210, sm: 220, md: 250 },
    borderRadius: 2,
    overflow: 'hidden',
    border: '1px solid',
    backgroundColor: '#070707',
    boxShadow: '0 6px 18px rgba(15, 23, 42, 0.12)'
  },
  coverPreview: {
    width: '100%',
    height: '100%',
    display: 'grid',
    placeItems: 'center',
    backgroundColor: '#070707'
  },
  bannerShade: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(90deg, rgba(7,7,7,0.78) 0%, rgba(7,7,7,0.46) 46%, rgba(7,7,7,0.10) 100%)'
  },
  bannerContent: {
    position: 'absolute',
    left: { xs: 18, md: 128 },
    right: { xs: 18, md: 28 },
    bottom: { xs: 22, md: 32 },
    maxWidth: { xs: 'calc(100% - 36px)', md: 620 },
    color: '#fff'
  },
  bannerChip: {
    backgroundColor: 'rgba(255,255,255,0.14)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.18)',
    '& .MuiChip-icon': { color: '#f2cc38' }
  },
  bannerTitle: {
    fontWeight: 900,
    fontSize: { xs: 26, md: 38 },
    lineHeight: 1,
    color: '#fff',
    textShadow: '0 2px 8px rgba(0,0,0,0.32)'
  },
  bannerSubtitle: {
    mt: 1,
    maxWidth: 560,
    color: 'rgba(255,255,255,0.82)',
    fontSize: { xs: 14, md: 17 },
    fontWeight: 700
  },
  headerMotif: {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: '#070707',
    backgroundImage: 'radial-gradient(circle at 18% 22%, rgba(201, 162, 39, 0.28) 0 2px, transparent 3px), radial-gradient(circle at 72% 34%, rgba(255, 255, 255, 0.18) 0 1px, transparent 2px), linear-gradient(135deg, rgba(255,255,255,0.08) 0 1px, transparent 1px), linear-gradient(135deg, #070707 0%, #151515 48%, #2a2110 100%)',
    backgroundSize: '34px 34px, 44px 44px, 18px 18px, 100% 100%'
  },
  headerMotifBand: {
    position: 'absolute',
    inset: 'auto -8% 18% -8%',
    height: 52,
    transform: 'rotate(-5deg)',
    background: 'linear-gradient(90deg, rgba(201,162,39,0.92), rgba(247,242,231,0.92), rgba(201,162,39,0.72))',
    opacity: 0.9
  },
  headerMotifMark: {
    position: 'absolute',
    right: { xs: 18, md: 28 },
    top: { xs: 18, md: 24 },
    color: '#F7F2E7',
    fontSize: { xs: 28, md: 42 },
    fontWeight: 900,
    letterSpacing: 0
  },
  headerMotifMeta: {
    position: 'absolute',
    right: { xs: 18, md: 30 },
    top: { xs: 54, md: 74 },
    maxWidth: { xs: 220, md: 420 },
    color: '#C9A227',
    fontSize: { xs: 13, md: 16 },
    fontWeight: 800,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  coverImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  logoPreview: {
    position: 'absolute',
    left: 18,
    bottom: { xs: 154, md: 30 },
    width: { xs: 76, md: 92 },
    height: { xs: 76, md: 92 },
    borderRadius: 3,
    border: '3px solid',
    backgroundColor: '#070707',
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
    boxShadow: '0 14px 36px rgba(0, 0, 0, 0.36)'
  },
  logoFallback: {
    color: '#C9A227',
    fontWeight: 900,
    fontSize: { xs: 24, md: 28 },
    letterSpacing: 0
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  dishImageBox: {
    width: 84,
    height: 84,
    borderRadius: 2,
    overflow: 'hidden',
    backgroundColor: '#eef2f6',
    display: 'grid',
    placeItems: 'center',
    flex: '0 0 auto'
  },
  dishImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  settingsBox: {
    p: 2,
    minHeight: 120,
    borderRadius: 3,
    backgroundColor: '#fff'
  },
  mediaDrop: {
    mt: 1,
    p: 1.5,
    borderRadius: 3,
    borderStyle: 'dashed',
    transition: 'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
    backgroundColor: '#fbfcfd',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.72)'
  },
  mediaPreview: {
    width: { xs: '100%', md: 96 },
    height: { xs: 150, md: 96 },
    borderRadius: 2,
    overflow: 'hidden',
    backgroundColor: '#eef2f6',
    display: 'grid',
    placeItems: 'center',
    flex: '0 0 auto'
  },
  mediaPreviewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  emptyState: {
    py: 6,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1,
    textAlign: 'center'
  },
  zoomIconButton: {
    color: '#fff',
    backgroundColor: 'rgba(255,255,255,0.12)',
    alignSelf: { xs: 'flex-end', md: 'center' },
    '&:hover': { backgroundColor: 'rgba(212,175,55,0.35)' }
  },
  zoomDialog: {
    '& .MuiDialog-paper': {
      borderRadius: { xs: 0, sm: 3 }
    }
  },
  zoomHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 1.5,
    p: 2.5,
    backgroundColor: '#111318'
  },
  zoomHeaderTitle: {
    fontWeight: 900,
    fontSize: { xs: 22, sm: 26 },
    color: '#fff'
  },
  zoomHeaderMeta: {
    color: '#c4c9d0',
    fontSize: 14,
    mt: 0.5
  },
  zoomContent: {
    p: { xs: 2, sm: 3 }
  },
  zoomCustomer: {
    fontWeight: 800,
    fontSize: 18,
    color: '#172033'
  },
  zoomAddress: {
    fontSize: 16,
    color: '#52606f'
  },
  zoomPickupCode: {
    mt: 1.5,
    p: 2,
    borderRadius: 2,
    border: '2px solid #b8941f',
    backgroundColor: '#faf3d9',
    textAlign: 'center'
  },
  zoomSectionTitle: {
    fontWeight: 900,
    fontSize: 18,
    color: '#172033'
  },
  zoomItemRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    p: 1.5,
    borderRadius: 2,
    backgroundColor: '#f8fafc',
    border: '1px solid #edf0f3'
  },
  zoomItemQty: {
    minWidth: 44,
    height: 44,
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    backgroundColor: '#111318',
    color: '#d4af37',
    fontWeight: 900,
    fontSize: 18,
    flex: '0 0 auto'
  },
  zoomItemName: {
    fontWeight: 800,
    fontSize: { xs: 18, sm: 20 },
    color: '#172033',
    lineHeight: 1.25
  },
  zoomItemOption: {
    fontSize: 14,
    color: '#667085',
    mt: 0.25
  },
  zoomItemPrice: {
    fontWeight: 900,
    fontSize: 18,
    color: '#172033',
    whiteSpace: 'nowrap'
  },
  zoomPriceLine: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2
  },
  zoomPriceLabel: {
    fontSize: 16,
    color: '#52606f'
  },
  zoomPriceValue: {
    fontSize: 16,
    fontWeight: 800,
    color: '#172033'
  },
  zoomPriceTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    mt: 1,
    pt: 1.5,
    borderTop: '2px solid #111318'
  },
  zoomPriceTotalLabel: {
    fontSize: 18,
    fontWeight: 900,
    color: '#172033'
  },
  zoomPriceTotalValue: {
    fontSize: 22,
    fontWeight: 900,
    color: '#8a6d16'
  },
  zoomActions: {
    p: { xs: 2, sm: 2.5 },
    gap: 1,
    flexWrap: 'wrap',
    borderTop: '1px solid #edf0f3',
    '& .MuiButton-root': {
      minHeight: 52,
      borderRadius: 2,
      fontWeight: 900
    }
  }
};
